import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { normalizeApiError, UNKNOWN_ERROR_CODE } from '../types/api-errors';
import { ProfileType, ServiceType, Gender } from '../types/enums';
import { AppointmentStatus } from '../types/enums';
import type {
    StaffAppointmentListParams,
    StaffAppointmentListResponse,
    StaffAppointmentDetailDto,
    OwnerAppointmentListParams,
    OwnerAppointmentListResponse,
    ServiceListResponse,
    ServiceDetailsResponse,
    PromotionListResponse,
    PackageListResponse,
    PackageRequest,
    PromotionDto,
    BranchDto,
    BranchHourDto,
    BranchHoursResponse,
    EffectiveBranchHoursResponse,
    SetBusinessHoursBulkRequest,
    SetBusinessHoursBulkResponse,
    BusinessBranchListResponse,
    Category,
    CategoryTargetGender,
    CategoryListResponse,
    CategoryAdminListMode,
    CategoryDeactivateImpact,
    CategoryImpactPage,
    CategoryImpactSalonRow,
    CategoryImpactServiceRow,
    CategoryImpactBookingRow,
    BusinessDetailsResponse,
    BusinessStaffListResponse,
    RequestCategoryResponse,
    CreateServiceResponse,
    DashboardSummaryResponse,
    LogoutResponse
} from '../types/api-responses';
import type { OptionGroupWirePayload } from '../types/serviceOptionGroups';
import type { NotificationListResponse, UnreadCountResponse } from '../types/notifications';
import { DiscountType, CouponCodeType } from '../types/api-responses';
import type { CustomerListResponse, CustomerDetailDto } from '../types/customers';
import type { User } from '../types/user.interface';
import type {
    PlatformPromotionDto,
    PlatformPromotionListResponse,
    PlatformPromotionListParams,
    PlatformPromotionUpsertRequest,
    DuplicatePlatformPromotionRequest,
    PlatformPromotionAnalyticsDto,
    PlatformPromotionAuditEntryDto,
    PlatformPromotionsConfigDto,
} from '../types/platformPromotions';

/**
 * RFC-4122 v4 UUID for the Idempotency-Key header on mutations (AB#1006).
 * Uses crypto.randomUUID when available, with a deterministic-enough fallback
 * for older runtimes.
 */
function newIdempotencyKey(): string {
    const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
    if (c?.randomUUID) return c.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
        const r = (Math.random() * 16) | 0;
        const v = ch === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

// Extend Axios config to support the retry flag used by the 401 interceptor
interface RetryableRequestConfig extends InternalAxiosRequestConfig {
    _retry?: boolean;
}

export interface BusinessRequest {
    name: string;
    about: string;
    businessRegistrationNumber?: string;
    isAlwaysOpen: boolean;
    serviceType: ServiceType;
    city: string;
    state: string;
    streetAddress: string;
    zipCode: string;
    hasHomeService: boolean;
    latitude?: number;
    longitude?: number;
    country: string;
    businessHours: Array<{
        dayOfWeek: number;
        isOpen: boolean;
        openTime?: string;
        closeTime?: string;
        is24Hours: boolean;
    }>;
    categoryIds: number[];
}

export interface InvitationDetailsResponse {
    invitationId: number;
    fullName: string;
    email: string;
    phoneNumber: string;
    gender?: Gender;
    position: string;
    businessName: string;
    role: 1 | 2;
    expiresAt: string;
}

export interface AcceptInvitationRequest {
    token: string;
    password: string;
    confirmPassword: string;
}

export interface AcceptInvitationResponse {
    staffId: number;
    message: string;
}

/**
 * Auth v2 — descriptor returned in availableProfiles + GetProfilesV2.
 * Mirrors the backend `AvailableProfileDto` from
 * `Devanza.Services/Contracts/Auth/LoginV2RequestResponse.cs`.
 */
export interface AvailableProfileDto {
    id: number;
    type: ProfileType;
    businessId?: number | null;
    businessName?: string | null;
}

export interface LoginV2Response {
    userId: number;
    accessToken: string;
    refreshToken: string;
    accessTokenExpiresAt: string;
    refreshTokenExpiresAt: string;
    /** Null when the user has >1 profile and no LastActiveProfileId — client shows chooser. */
    activeProfileId: number | null;
    availableProfiles: AvailableProfileDto[];
    /**
     * AB#1612 Phase 1A.2 (CR-2026-05-03 — soften login gate). True when the
     * caller logged in with an email identifier whose `EmailVerified` flag is
     * still false. The login itself succeeds (soft gate per PO direction in
     * wiki page 78); branch/staff endpoints continue to gate independently
     * with 403 `email_verification_required` until verify-email completes.
     * The FE renders a banner with a "Resend verification email" CTA when
     * this is true.
     */
    emailVerificationRequired?: boolean;
}

/** AB#1612 Phase 1B — request a verification email be sent to the
 *  authenticated user's address. Endpoint: POST /v2/auth/request-email-
 *  verification. Requires JWT (must be signed-in). 429 on rate-limit. */
export interface RequestEmailVerificationAuthRequest {
    email: string;
}
export interface RequestEmailVerificationAuthResponse {
    message: string;
}

/** AB#1612 Phase 1B — verify the token from the email link. Endpoint:
 *  POST /v2/auth/verify-email. JWT optional (link can be clicked from a
 *  fresh browser); BE matches token to FormCredential. */
export interface VerifyEmailRequestDto {
    email: string;
    token: string;
}
export interface VerifyEmailResponseDto {
    message: string;
    emailVerified: boolean;
}

export interface SwitchProfileResponse {
    accessToken: string;
    accessTokenExpiresAt: string;
    activeProfileId: number;
}

export interface VerifyPasswordResponse {
    accessToken: string;
    accessTokenExpiresAt: string;
}

export interface VerifyAccountRequest {
    userId: number;
    userType: number;
    verificationToken: string;
}

export interface AppointmentRequest {
    businessId: number;
    clientName: string;
    mobileNumber: string;
    email: string;
    categoryId: number;
    serviceId: number;
    pricingOptionId: number;
    amount: number;
    staffId?: number | null;
    paymentStatus: string;
    status?: string;
    appointmentDate: string;
    isDraft: boolean;
    notes?: string;
    /**
     * PBI 1962 (AB#1962) — option-group selection. ADDITIVE + OPTIONAL: both
     * keys are OMITTED for legacy no-group services (byte-identical requests).
     * When present and the backend `service_option_groups` flag is ON, the
     * server validates the selection (VARIANT_REQUIRED / OPTION_SELECTION_INVALID)
     * and its computed total OVERRIDES the client `amount`.
     */
    variantOptionId?: number;
    addOnOptionIds?: number[];
}

/**
 * PBI 1962 Phase 2 — one itemized line on the owner appointment detail
 * (`AppointmentSelectedOptionDto`). `kind` serializes as the enum NAME.
 * Price/duration are AS APPLIED (absolute for the variant, delta for add-ons).
 * Legacy scalar-only appointments get a synthesized fallback line whose
 * `serviceOptionId` is the legacy ServicePricingOption id (not a ServiceOption).
 */
export interface AppointmentSelectedOptionLineDto {
    serviceOptionId: number;
    serviceOptionGroupId: number;
    kind: 'Variant' | 'AddOn';
    name: string;
    price: number;
    /** "HH:mm:ss" TimeSpan string. */
    duration: string;
    sortOrder: number;
}

export interface AppointmentResponse {
    id: number;
    businessId?: number;
    clientName?: string;
    customerName?: string;
    mobileNumber?: string;
    clientPhone?: string;
    phoneNumber?: string;
    email?: string;
    clientEmail?: string;
    serviceId: number;
    categoryId?: number;
    businessServiceId?: number;
    servicePricingOptionId?: number;
    pricingOptionId?: number;
    servicePrice?: number;
    amount: number;
    appointmentDate: string;
    appointmentTime?: string;
    isDraft: boolean;
    status: string;
    staffId?: number;
    paymentStatus?: number | string;
    appointmentStatus?: string;
    notes?: string;
    /**
     * PBI 1962 Phase 2 — itemized option selection (variant line + add-on
     * lines) on GET /salon-owner/appointments/{id}. Used to pre-select the
     * option picker in the edit flow.
     */
    selectedOptions?: AppointmentSelectedOptionLineDto[];
}

export interface StaffListParams {
    page?: number;
    pageSize?: number;
    search?: string;
    branchId?: number;
    businessId?: number;
    // CR-2026-05-16-v1 — restrict to staff who can perform a specific service.
    serviceId?: number;
    // CR-2026-05-16-v1 — include inactive staff in the list (defaults to true on BE).
    includeInactive?: boolean;
}

export interface RegisterStaffRequest {
    businessId?: number;
    fullName: string;
    email: string;
    phoneNumber: string;
    position?: string;
    role?: number;
    gender?: Gender;
    isActive?: boolean;
    branchIds?: number[];
}

// Batch B (AB#2176) — create a bookable staff record without a login.
export interface CreateStaffRecordRequest {
    fullName: string;
    gender?: Gender;
    position?: string;
    role?: number;
    isActive?: boolean;
}

// Batch B (AB#2176) — optional portal invitation for an existing staff record.
export interface InviteStaffRecordRequest {
    email: string;
    phoneNumber: string;
}

export interface UpdateStaffRequest {
    fullName?: string;
    email?: string;
    phoneNumber?: string;
    position?: string;
    isActive?: boolean;
    role?: 1 | 2;
    gender?: Gender;
}

export interface TogglePromotionRequest {
    isActive: boolean;
}

/**
 * AB#1818 — Super Admin create category request. Multipart payload accepted
 * by `POST /api/Category/admin/create`. `isActive` controls whether the new
 * category is published immediately (Dina spec §5.4 "Active immediately" toggle).
 * AB#1870 — `targetGender` (ordinal) sets the gender-targeting hint; omitted
 * defaults to `NoPreference (0)` server-side.
 */
export interface AdminCreateCategoryRequest {
    name: string;
    nameAr?: string;
    image: File;
    isActive: boolean;
    targetGender?: CategoryTargetGender;
}

/**
 * AB#1823 — SuperAdmin edit-category request. Multipart payload accepted by
 * `PUT /api/Category/admin/{id}`. `image` is sent ONLY when a new file is
 * picked; omit it to preserve the existing `imageUrl`. AB#1870 — `targetGender`
 * (ordinal) updates the gender-targeting hint; omitted preserves the stored value.
 */
export interface AdminUpdateCategoryRequest {
    name: string;
    nameAr: string;
    image?: File;
    isActive: boolean;
    targetGender?: CategoryTargetGender;
}

export interface ServicePricingOptionDto {
    name: string;
    price: number;
    currency: string;
    duration: string;
}

export interface ServiceRequest {
    categoryId: number;
    branchId: number;
    name: string;
    image?: File;
    minDuration: string;
    maxDuration: string;
    serviceType: number;
    /**
     * AB#1847 (PBI-I) / AB#1838 — Age axis of the gender matrix. BE accepts
     * the canonical string form per Faris F-Q2=A. Required on create —
     * the owner must explicitly pick Adult / Kid / Both on the wizard.
     */
    ageGroup: 'Adult' | 'Kid' | 'Both';
    hasHomeService: boolean;
    description?: string;
    pricingOptions: ServicePricingOptionDto[];
    /**
     * PBI 1962 (AB#1968) — flexible option groups (variants & add-ons), sent
     * as the multipart `OptionGroupsJson` field ALONGSIDE `PricingOptionsJson`.
     * Optional & additive: when omitted the backend auto-wraps the flat
     * pricing options into an implicit Variant group (exact legacy behavior),
     * so the field is only populated when the web option-groups builder flag
     * is ON. See `src/types/serviceOptionGroups.ts` (OptionGroupWirePayload).
     */
    optionGroups?: OptionGroupWirePayload[];
    staffIds?: number[];
}

export interface UpdateServiceRequest {
    branchId: number;
    /**
     * B16 — Optional category re-parent. Send when the owner wants to move
     * a service from its current category to a different one. BE validates
     * the target category belongs to the caller's business and that moving
     * wouldn't create a name collision in the target category.
     */
    categoryId?: number;
    name: string;
    image?: File;
    removeExistingImage: boolean;
    minDuration: string;
    maxDuration: string;
    serviceType: number;
    /**
     * AB#1847 (PBI-I) / AB#1838 — Age axis of the gender matrix. Required
     * on update — the value is always sent (even if unchanged) so the BE
     * can apply atomic-replace semantics. See `AgeGroup` in api-responses.ts.
     */
    ageGroup: 'Adult' | 'Kid' | 'Both';
    hasHomeService: boolean;
    description?: string;
    isActive: boolean;
    pricingOptions: ServicePricingOptionDto[];
    /**
     * PBI 1962 (AB#1968) — flexible option groups. Same additive/back-compat
     * semantics as on {@link ServiceRequest}; sent as `OptionGroupsJson`.
     */
    optionGroups?: OptionGroupWirePayload[];
    staffIds?: number[];
}

export interface CreateDiscountedDatePromoRequest {
    branchId?: number;
    name: string;
    startDate: string;
    endDate: string;
    discountValue: number;
    discountType: DiscountType;
}

export interface UpdateDiscountedDatePromoRequest {
    branchId?: number;
    name: string;
    startDate: string;
    endDate: string;
    discountValue: number;
    discountType: DiscountType;
    isActive: boolean;
}

export interface CreateServiceLevelPromoRequest {
    branchId?: number;
    name: string;
    serviceId: number;
    minimumAmount?: number;
    discountValue: number;
    discountType: DiscountType;
}

export interface UpdateServiceLevelPromoRequest {
    branchId?: number;
    name: string;
    serviceId: number;
    minimumAmount?: number;
    discountValue: number;
    discountType: DiscountType;
    isActive: boolean;
}

export interface CreateCouponPromoRequest {
    couponName: string;
    couponQuantity: number;
    expiryDate: string;
    discountValue: number;
    discountType: DiscountType;
    codeType: CouponCodeType;
}

export interface UpdateCouponPromoRequest {
    couponName: string;
    couponQuantity: number;
    expiryDate: string;
    discountValue: number;
    discountType: DiscountType;
    codeType: CouponCodeType;
    isActive: boolean;
}

export interface CreateBranchRequest {
    name: string;
    streetAddress: string;
    city: string;
    state: string;
    country: string;
    latitude?: number;
    longitude?: number;
    phoneNumber: string;
    primaryHeadQuarter: boolean;
    active: boolean;
    description: string;
}

export interface UpdateBranchRequest {
    id: number;
    name: string;
    streetAddress: string;
    city: string;
    state: string;
    country: string;
    latitude?: number;
    longitude?: number;
    phoneNumber: string;
    primaryHeadQuarter: boolean;
    active: boolean;
    description: string;
}

export interface UpdateBusinessRequest {
    name?: string;
    about?: string;
    isAlwaysOpen?: boolean;
    serviceType?: ServiceType;
    // Legacy top-level location fields — kept for the OnboardingWizard
    // call site. AB#1682 form sends these inside `location` instead.
    country?: string;
    city?: string;
    state?: string;
    streetAddress?: string;
    zipCode?: string;
    hasHomeService?: boolean;
    latitude?: number;
    longitude?: number;
    /** AB#1682 — preferred nested shape. BE BusinessLocationDto accepts
     *  these. Used by BusinessDetailsSection.tsx; legacy onboarding sends
     *  the same fields top-level. */
    location?: {
        country?: string;
        city?: string;
        state?: string;
        streetAddress?: string;
        zipCode?: string;
        hasHomeService?: boolean;
        latitude?: number;
        longitude?: number;
        // AB#1682 — social channels persisted on BusinessLocation row.
        instagram?: string;
        whatsApp?: string;
        website?: string;
        facebook?: string;
        twitter?: string;
        tikTok?: string;
        googleMapsUrl?: string;
    };
    businessHours?: Array<{ dayOfWeek: number; isOpen: boolean; openTime?: string; closeTime?: string; is24Hours: boolean }>;
    categoryIds?: number[];

    // ── AB#1682 — Bilingual + direct contact (null = no change) ─────────
    nameAR?: string;
    aboutAR?: string;
    email?: string;
    phone?: string;
    website?: string;

    // ── AB#1682 — Country/Currency FK (optional; null = no change) ──────
    countryId?: number;
    currencyId?: number;

    // ── AB#1682 — Multi-type Business Types (replace-the-set, max 4) ────
    businessTypeIds?: number[];

    // ── AB#1682 — Booking config + display preferences ──────────────────
    settings?: {
        pricingDecimalPlaces: number;
        autoConfirmBookings: boolean;
        allowWalkIns: boolean;
        showRatings: boolean;
    };
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://devanza-dev-backend.azurewebsites.net/api';

export interface LoginRequest {
    identifier: string;
    password: string;
    userType: ProfileType;
}

export interface RegisterRequest {
    email: string;
    password: string;
    fullName: string;
    phoneNumber?: string;
    countryCode?: string;
    gender?: Gender;
    termsAccepted: boolean;
    userType: ProfileType;
}

// ── OTP v2 — salon owner self-registration (AB#1451 / PO-D-012) ─────────────

/** POST /api/v2/auth/otp/request */
export interface OtpRequestRequest {
    identifier: string;
    channel: 'sms' | 'email';
    purpose: 'login' | 'signup' | 'reset';
}

export interface OtpRequestResponse {
    requestId: string;
    resendIn: number;
    expiresIn: number;
    channel: string;
}

/** POST /api/v2/auth/otp/verify
 *
 * AB#1568 cleanup: dead `fullName` and `termsAccepted` fields removed —
 * the BE contract (`OtpVerifyV2Request`) only has RequestId, Code,
 * Identifier. Anything else was being silently dropped. Captured at
 * Step 3 instead and sent to /v1/auth/register.
 */
export interface OtpVerifyRequest {
    requestId: string;
    code: string;
    identifier: string;
}

export interface OtpVerifyUserSummary {
    userId: number;
    fullName: string;
}

/**
 * AB#1568 — corrected to match the actual BE contract
 * (`Devanza.Services/Contracts/Auth/V2/OtpVerifyV2Response.cs`).
 *
 * Critical fixes vs. the previous shape:
 *   - `tokenType` is lowercase + snake_case (`bearer` / `signup_scoped` /
 *     `reset_scoped`), NOT PascalCase. The previous interface
 *     ('Bearer' | 'SignupScoped') silently broke every signup attempt.
 *   - `refreshToken` is optional (absent on signup_scoped).
 *   - `resetToken` added (populated on reset_scoped).
 */
export interface OtpVerifyResponse {
    tokenType: 'bearer' | 'signup_scoped' | 'reset_scoped';
    accessToken: string;
    refreshToken?: string | null;
    resetToken?: string | null;
    expiresIn: number;
    isNewUser: boolean;
    requiresProfileCompletion: boolean;
    user: OtpVerifyUserSummary | null;
    allowedScopes: string[] | null;
}

export interface InitiatePasswordResetRequest {
    identifier: string;
    // userType: ProfileType; // not used by POST /api/v2/auth/request-reset
}

export interface ResetPasswordRequest {
    userId: number;
    resetToken: string;
    newPassword: string;
}

export interface InitiatePasswordResetResponse {
    userId: number;
}

export interface UserProfileResponseDto {
    id: number;
    fullName: string;
    email: string;
    phoneNumber: string;
    countryCode: string;
    profileType: string;
}

export interface UpdateUserProfileRequest {
    userProfileId: number;
    fullName: string;
    email: string;
    phoneNumber: string;
    countryCode: string;
}

export interface ChangePasswordRequestDto {
    userProfileId: number;
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
}

export interface LogoutRequest {
    refreshToken: string;
    logoutFromAllDevices: boolean;
}

/* ── AB#2001 / AB#2002 — dayOfWeek read-boundary normalizer ──────────────── */

/**
 * The BE serializes System.DayOfWeek as the enum NAME ("Sunday".."Saturday")
 * via the global JsonStringEnumConverter (AB#1894 wire convention), but FE
 * consumers index days by 0..6 int (e.g. ApprovalDetailPage's `DAYS[dow]`,
 * EmployeeModal's `getDayName(dayOfWeek)`). Coerce at the service boundary so
 * every current + future consumer sees numeric days — shared by
 * getPendingApprovalDetail businessHours (AB#2001) and getBusinessStaff
 * staff-schedules (AB#2002, where the string produced blank "Work Schedule"
 * day labels). Idempotent: ints pass through; enum names map; numeric strings
 * parse; unknown → -1 (a non-day sentinel — `days[-1]` degrades to a blank
 * label rather than a wrong day). Matches the shape Faris named
 * (useBranchHours.ts, -1 default) plus the numeric-string branch from
 * ScheduleTab.tsx. Per-file local by design (arch-brief §6 — shared helper
 * deferred as tech-debt). Exported for unit coverage.
 */
const BUSINESS_HOURS_DAY_NAME_TO_INDEX: Record<string, number> = {
    Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3,
    Thursday: 4, Friday: 5, Saturday: 6,
};

export function normalizeDayOfWeek(dow: string | number): number {
    if (typeof dow === 'number') return dow;
    const byName = BUSINESS_HOURS_DAY_NAME_TO_INDEX[dow];
    if (typeof byName === 'number') return byName;
    const parsed = parseInt(dow, 10);
    return Number.isNaN(parsed) ? -1 : parsed;
}

/**
 * AB#2001 — normalize a pending-approval detail's `businessHours[].dayOfWeek`
 * from the wire enum name to the 0..6 int the FE indexes by. Returns a new
 * object; all other fields pass through untouched.
 */
function normalizePendingApprovalDetail(detail: PendingApprovalDetail): PendingApprovalDetail {
    return {
        ...detail,
        // AB#2108 — the page maps over these; default defensively.
        services: detail.services ?? [],
        documents: detail.documents ?? [],
        businessHours: (detail.businessHours ?? []).map((h) => ({
            ...h,
            dayOfWeek: normalizeDayOfWeek(h.dayOfWeek as unknown as string | number),
        })),
    };
}

/**
 * AB#2002 — normalize a business-staff list's `staff[].schedules[].dayOfWeek`
 * from the wire enum name to the 0..6 int the FE indexes by. EmployeeModal's
 * `getDayName(schedule.dayOfWeek)` int-indexes `['Sunday'..'Saturday']`, so a
 * string produced `days['Sunday']` = undefined → blank "Work Schedule" labels
 * (only visible when editing an existing staff member). Normalizing at this
 * shared read boundary protects every consumer of getBusinessStaff. Returns
 * new objects; `dayOfWeek` is the only field touched (gender/role et al. pass
 * through untouched — the static type stays numeric). Null-safe on both
 * `staff` and `schedules`.
 */
function normalizeBusinessStaffList(res: BusinessStaffListResponse): BusinessStaffListResponse {
    return {
        ...res,
        staff: (res.staff ?? []).map((s) => ({
            ...s,
            schedules: (s.schedules ?? []).map((sch) => ({
                ...sch,
                dayOfWeek: normalizeDayOfWeek(sch.dayOfWeek as unknown as string | number),
            })),
        })),
    };
}

class ApiService {
    private axiosInstance: AxiosInstance;

    // Single-flight silent refresh. When several requests 401 at once (e.g. an
    // admin Edit PUT and its `['admin','categories']` refetch firing together),
    // they must all await ONE `/auth/refresh` call rather than each firing its
    // own. The backend rotates (single-uses) the refresh token, so parallel
    // refreshes make all-but-one fail with an invalid-token 401 — and that
    // failure path clears tokens and dispatches `auth:session-expired`,
    // bouncing the user to /form/login even though a sibling refresh succeeded
    // and the original write persisted. Holding the in-flight promise here lets
    // concurrent 401s share the single rotation. Reset to null once settled.
    private refreshPromise: Promise<string> | null = null;

    constructor() {
        this.axiosInstance = axios.create({
            baseURL: API_BASE_URL
        });

        const token = localStorage.getItem('accessToken');
        if (token) {
            this.axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        }

        // Attach token from localStorage on every request (ensures token is sent after login/refresh)
        this.axiosInstance.interceptors.request.use(
            (config) => {
                const accessToken = localStorage.getItem('accessToken');
                if (accessToken) {
                    config.headers.Authorization = `Bearer ${accessToken}`;
                }
                return config;
            },
            (error) => Promise.reject(error)
        );

        // Auth endpoint paths that must never trigger session-expiry handling
        const AUTH_ENDPOINTS = [
            '/auth/login', '/auth/register', '/auth/refresh',
            '/auth/social', '/v2/auth/request-reset', '/auth/reset-password',
            '/Auth/logout', '/Auth/verify',
            // OTP registration endpoints (AB#1451) — unauthenticated by design
            '/v2/auth/otp/request', '/v2/auth/otp/verify',
        ];

        // Response error interceptor:
        //  1. On 401 for non-auth endpoints: attempt a silent token refresh.
        //     - If refresh succeeds: update tokens and retry the original request.
        //     - If refresh fails (or no refresh token): clear tokens and dispatch
        //       'auth:session-expired' so AuthContext can update React state.
        //  2. Extract backend error code for better user-facing messages.
        this.axiosInstance.interceptors.response.use(
            (response) => response,
            async (error) => {
                const originalRequest = error.config as RetryableRequestConfig | undefined;
                const isAuthEndpoint = AUTH_ENDPOINTS.some(ep =>
                    originalRequest?.url?.toLowerCase().includes(ep.toLowerCase())
                );

                if (
                    error.response?.status === 401 &&
                    originalRequest &&
                    !originalRequest._retry &&
                    !isAuthEndpoint
                ) {
                    originalRequest._retry = true;
                    try {
                        // Single-flight: the first 401 starts the refresh and
                        // parks the in-flight promise on `this.refreshPromise`;
                        // any sibling 401 racing the same token swap reuses it
                        // instead of firing its own `/auth/refresh`. This keeps
                        // the (single-use, rotated) refresh token to exactly one
                        // exchange, so a successful refresh never trips a sibling
                        // into the session-expired catch below. See the field
                        // doc on `refreshPromise`.
                        const newAccess = await this.startTokenRefresh();
                        originalRequest.headers.Authorization = `Bearer ${newAccess}`;
                        return this.axiosInstance(originalRequest);
                    } catch {
                        // Refresh genuinely failed (no/expired refresh token, or
                        // a malformed response) — end the session.
                        this.clearTokens();
                        window.dispatchEvent(new Event('auth:session-expired'));
                    }
                }

                // AB#1828 / AB#1862 — surface structured error envelope onto
                // the thrown axios error so caller hooks can branch on a typed
                // `code` + access the structured `details` payload without
                // re-inspecting `response.data` themselves.
                //
                // Two BE shapes are supported here:
                //   1. Top-level `{ code: "Slot.BRANCH_CLOSED" }` (AB#1828
                //      precedent — the canonical `Error` factory output).
                //   2. Nested `{ details: { code: "STAFF_REASSIGN_HAS_ACTIVE_BOOKINGS", ... } }`
                //      (AB#1862 — atomic-replace conflict-guards expose a
                //      structured payload with `count`, `earliestAppointmentDate`,
                //      `affectedBranchIds`, `affectedServiceIds`, `handler`).
                // When both are present, top-level wins for `code` (it's the
                // primary contract); `details` is always attached when nested.
                // AB#1919 (WS-5b) — the canonical AppError is produced HERE,
                // once, by the single normalizer. It reads `errorCode ?? code`
                // (§3 emitter divergence: ExceptionMiddleware emits `errorCode`,
                // ResultExtensions emits `code`) and prefers the RFC 7807 fields
                // (`status`/`detail`/`type`/`title`/`instance`) with a legacy
                // fallback. Stamped onto the rejected error as `error.appError`.
                const data = error.response?.data as
                    | {
                          code?: string;
                          details?: { code?: string; [k: string]: unknown };
                          _codeProcessed?: boolean;
                      }
                    | undefined;
                if (data && !data._codeProcessed) {
                    const appError = normalizeApiError(error);
                    // Nested `{ details: { code } }` (AB#1862 atomic-replace
                    // conflict guards) still wins when the top-level body has no
                    // own code — the normalizer only reads top-level keys.
                    const nestedCode = data.details?.code;
                    const effectiveCode =
                        appError.code !== UNKNOWN_ERROR_CODE ? appError.code : nestedCode;
                    if (effectiveCode) {
                        (error as { code?: string }).code = effectiveCode;
                    }
                    // Attach the structured `details` payload (AB#1862) unchanged.
                    if (data.details) {
                        (error as { details?: unknown }).details = data.details;
                    }
                    // Expose the full canonical error for richer consumers.
                    (error as { appError?: typeof appError }).appError = appError;
                    data._codeProcessed = true;
                }
                return Promise.reject(error);
            }
        );
    }

    setTokens(accessToken: string, refreshToken: string) {
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
        this.axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    }

    clearTokens() {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        delete this.axiosInstance.defaults.headers.common['Authorization'];
    }

    /**
     * Exchange the stored refresh token for a fresh access token. Resolves to
     * the new access token; rejects if there is no refresh token or the backend
     * declines. Callers MUST funnel through `this.refreshPromise` so concurrent
     * 401s share a single rotation (see the `refreshPromise` field doc). The
     * `finally` clears the slot so the next genuine expiry can refresh again.
     */
    private async performTokenRefresh(): Promise<string> {
        try {
            const storedRefreshToken = localStorage.getItem('refreshToken');
            if (!storedRefreshToken) {
                throw new Error('No refresh token available');
            }
            // AB#1617 — BE returns the refresh response FLAT (no { data }
            // envelope): { accessToken, refreshToken, accessTokenExpiresAt,
            // refreshTokenExpiresAt }. Read the flat shape directly.
            const refreshResponse = await axios.post<{
                accessToken: string;
                refreshToken: string;
                accessTokenExpiresAt?: string;
                refreshTokenExpiresAt?: string;
            }>(
                `${API_BASE_URL}/auth/refresh`,
                { refreshToken: storedRefreshToken }
            );
            const { accessToken: newAccess, refreshToken: newRefresh } = refreshResponse.data;
            if (!newAccess || !newRefresh) {
                throw new Error('Refresh response missing tokens');
            }
            this.setTokens(newAccess, newRefresh);
            return newAccess;
        } finally {
            this.refreshPromise = null;
        }
    }

    async verifyAccount(data: VerifyAccountRequest): Promise<unknown> {
        const response = await this.axiosInstance.post('/Auth/verify', data);
        return response.data;
    }

    async login(data: LoginRequest) {
        const response = await this.axiosInstance.post<{ accessToken: string; refreshToken: string }>('/auth/login', data);
        if (response.data.accessToken && response.data.refreshToken) {
            this.setTokens(response.data.accessToken, response.data.refreshToken);
        }
        return response.data;
    }

    // -----------------------------------------------------------------
    // Auth v2 (AB#1429 — #1432). Behind REACT_APP_AUTH_V2_ENABLED.
    // Contract:
    //   POST /v2/auth/login           — { identifier, password } → { accessToken, refreshToken, activeProfileId, availableProfiles[] }
    //   POST /v2/auth/switch-profile  — { profileId }            → { accessToken, activeProfileId }
    //   POST /v2/auth/verify-password — { password }             → { accessToken }   (for SuperAdmin elevation)
    //   GET  /v2/auth/profiles        — ()                       → AvailableProfileDto[]
    // -----------------------------------------------------------------

    async loginV2(data: { identifier: string; password: string }): Promise<LoginV2Response> {
        const response = await this.axiosInstance.post<LoginV2Response>('/v2/auth/login', data);
        if (response.data.accessToken && response.data.refreshToken) {
            this.setTokens(response.data.accessToken, response.data.refreshToken);
        }
        return response.data;
    }

    async switchProfile(profileId: number): Promise<SwitchProfileResponse> {
        const response = await this.axiosInstance.post<SwitchProfileResponse>('/v2/auth/switch-profile', { profileId });
        if (response.data.accessToken) {
            const existingRefresh = localStorage.getItem('refreshToken');
            this.setTokens(response.data.accessToken, existingRefresh ?? '');
        }
        return response.data;
    }

    async verifyPassword(password: string): Promise<VerifyPasswordResponse> {
        const response = await this.axiosInstance.post<VerifyPasswordResponse>('/v2/auth/verify-password', { password });
        if (response.data.accessToken) {
            const existingRefresh = localStorage.getItem('refreshToken');
            this.setTokens(response.data.accessToken, existingRefresh ?? '');
        }
        return response.data;
    }

    async getProfilesV2(): Promise<AvailableProfileDto[]> {
        const response = await this.axiosInstance.get<AvailableProfileDto[]>('/v2/auth/profiles');
        return response.data;
    }

    // ── OTP v2 — salon owner self-registration (AB#1451 / PO-D-012) ─────────

    async otpRequest(data: OtpRequestRequest): Promise<OtpRequestResponse> {
        const response = await this.axiosInstance.post<OtpRequestResponse>('/v2/auth/otp/request', data);
        return response.data;
    }

    async otpVerify(data: OtpVerifyRequest): Promise<OtpVerifyResponse> {
        const response = await this.axiosInstance.post<OtpVerifyResponse>('/v2/auth/otp/verify', data);
        return response.data;
    }

    // ── Email verification (AB#1612 Phase 1B — DEPLOYED) ────────────────────
    //
    // Both endpoints live behind `Auth:V2:Enabled` (currently true on dev).
    // Wiki: /AB1612-Phase-1B-Email-Verification-Commands. Soft-gate pattern —
    // login still succeeds for unverified emails (LoginV2Response.emailVerifi-
    // cationRequired = true), branch/staff endpoints 403 with
    // `email_verification_required` until verified.

    /**
     * Sends a verification email containing a token (24h expiry, bcrypt-
     * hashed) to the authenticated user's email. JWT REQUIRED — endpoint
     * is `[Authorize]`-protected by design.
     *
     * Errors:
     *  - 401 if no JWT (caller must be logged in)
     *  - 429 if rate-limited (5 requests/hour per credential)
     *  - 423 if email-verification is locked (5 failed verify attempts → 30min)
     */
    async requestEmailVerificationAuth(
        data: RequestEmailVerificationAuthRequest,
    ): Promise<RequestEmailVerificationAuthResponse> {
        const response = await this.axiosInstance.post<RequestEmailVerificationAuthResponse>(
            '/v2/auth/request-email-verification',
            data,
        );
        return response.data;
    }

    /**
     * Verifies the email token from the link clicked in the verification
     * email. The link the BE sends is:
     *   {frontendBaseUrl}/verify-email?email={url-encoded}&token={url-encoded}
     * On success the user's `EmailVerified` flag flips to true and any
     * subsequent /auth/me / login responses set emailVerificationRequired=false.
     *
     * Errors:
     *  - 400 invalid/expired token
     *  - 423 too many failed attempts (lockout)
     */
    async verifyEmail(data: VerifyEmailRequestDto): Promise<VerifyEmailResponseDto> {
        const response = await this.axiosInstance.post<VerifyEmailResponseDto>(
            '/v2/auth/verify-email',
            data,
        );
        return response.data;
    }

    // ──────────────────────────────────────────────────────────────────────────

    /**
     * POST /api/v1/auth/register.
     *
     * AB#1568 — when `attestationToken` is provided, it is sent as
     * `Authorization: Bearer <token>` so the BE can verify the OTP
     * attestation and skip the email-link verification step. Without it,
     * the legacy email-link flow runs unchanged.
     */
    async register(data: RegisterRequest, attestationToken?: string) {
        const config = attestationToken
            ? { headers: { Authorization: `Bearer ${attestationToken}` } }
            : undefined;
        const response = await this.axiosInstance.post('/auth/register', data, config);
        return response.data;
    }

    async socialAuth(data: { provider: number; idToken: string; userType: ProfileType; termsAccepted: boolean }) {
        const payload = { ...data, TermsAccepted: data.termsAccepted };
        const response = await this.axiosInstance.post<{ accessToken: string; refreshToken: string }>('/auth/social', payload);
        if (response.data?.accessToken && response.data?.refreshToken) {
            this.setTokens(response.data.accessToken, response.data.refreshToken);
        }
        return response.data;
    }

    async refreshAccessToken(refreshToken: string) {
        return await axios.post<{ data: { accessToken: string; refreshToken: string } }>(`${API_BASE_URL}/auth/refresh`, { refreshToken });
    }

    /**
     * AB#2108 — refresh the session through the SAME single-flight slot the 401
     * interceptor uses (see `refreshPromise`), so an explicit refresh — e.g. right
     * after creating a business, to pick up the new `businessId` claim — can never
     * race a silent refresh of the rotated, single-use refresh token. Resolves to
     * the new access token; rejects if the refresh fails.
     *
     * It deliberately does NOT return the refresh token: `performTokenRefresh`
     * already stored the rotated pair, and a caller that held a copy across an
     * `await` could write a consumed token back (forcing a re-login). Read the
     * current one with {@link getStoredRefreshToken} at the moment you write it.
     */
    async refreshSession(): Promise<{ accessToken: string }> {
        const accessToken = await this.startTokenRefresh();
        return { accessToken };
    }

    /** The refresh token as stored right now — never a copy captured earlier. */
    getStoredRefreshToken(): string {
        return localStorage.getItem('refreshToken') ?? '';
    }

    /**
     * Join the in-flight refresh or start one (see `refreshPromise`).
     * `performTokenRefresh` clears the slot in its own `finally`, but when it
     * rejects synchronously (no stored refresh token) that `finally` runs BEFORE
     * the promise is assigned here — so the slot is cleared again once the
     * promise settles, and can never stay stuck on an old rejection.
     */
    private startTokenRefresh(): Promise<string> {
        if (!this.refreshPromise) {
            const refresh = this.performTokenRefresh();
            this.refreshPromise = refresh;
            refresh.then(
                () => undefined,
                () => undefined,
            ).then(() => {
                if (this.refreshPromise === refresh) this.refreshPromise = null;
            });
        }
        return this.refreshPromise;
    }

    async requestPasswordReset(data: InitiatePasswordResetRequest): Promise<InitiatePasswordResetResponse> {
        const response = await this.axiosInstance.post<InitiatePasswordResetResponse>('/v2/auth/request-reset', data);
        return response.data;
    }

    async resetPassword(data: ResetPasswordRequest): Promise<boolean> {
        const response = await this.axiosInstance.post('/auth/reset-password', data);
        return response.status === 200;
    }

    async logout(data: LogoutRequest): Promise<LogoutResponse> {
        const response = await this.axiosInstance.post<LogoutResponse>('/Auth/logout', data);
        return response.data;
    }

    async getCurrentUser(): Promise<User> {
        const response = await this.axiosInstance.get<User>('/Auth/me');
        return response.data;
    }

    // User Profile APIs
    async getUserProfile(): Promise<UserProfileResponseDto> {
        const response = await this.axiosInstance.get('/user-profile');
        return response.data;
    }

    async updateUserProfile(data: Omit<UpdateUserProfileRequest, 'userProfileId'>): Promise<boolean> {
        const res = await this.axiosInstance.put('/user-profile', {
            fullName: data.fullName,
            email: data.email,
            phoneNumber: data.phoneNumber,
            countryCode: data.countryCode
        });
        return res.status === 200;
    }

    async changePassword(data: Omit<ChangePasswordRequestDto, 'userProfileId'>): Promise<boolean> {
        const res = await this.axiosInstance.post('/user-profile/change-password', {
            currentPassword: data.currentPassword,
            newPassword: data.newPassword,
            confirmPassword: data.confirmPassword
        });
        return res.status === 200;
    }

    // Notifications APIs (all authenticated roles)
    async getNotifications(limit?: number): Promise<NotificationListResponse> {
        const response = await this.axiosInstance.get('/notifications', {
            params: limit ? { limit } : undefined
        });
        return response.data;
    }

    async getUnreadNotificationCount(): Promise<UnreadCountResponse> {
        const response = await this.axiosInstance.get('/notifications/unread-count');
        return response.data;
    }

    // AB#2139 — the API's notifications controller routes PUT {id}/read and
    // PUT read-all; PATCH (and "mark-all-read") never reached it, so reading failed.
    async markNotificationAsRead(id: number): Promise<void> {
        await this.axiosInstance.put(`/notifications/${id}/read`);
    }

    async markAllNotificationsAsRead(): Promise<void> {
        await this.axiosInstance.put('/notifications/read-all');
    }

    async deleteNotification(id: number): Promise<void> {
        await this.axiosInstance.delete(`/notifications/${id}`);
    }

    async clearAllNotifications(): Promise<void> {
        await this.axiosInstance.delete('/notifications/clear-all');
    }

    // Notification Preferences APIs
    async getNotificationPreferences(): Promise<any> {
        const response = await this.axiosInstance.get('/notifications/preferences');
        return response.data;
    }

    async updateNotificationPreferences(preferences: any[]): Promise<any> {
        const response = await this.axiosInstance.put('/notifications/preferences', preferences);
        return response.data;
    }

    // Web Push Subscription APIs
    async subscribeWebPush(subscription: { endpoint: string; p256dhKey: string; authKey: string }): Promise<void> {
        await this.axiosInstance.post('/notifications/push/subscribe', subscription);
    }

    async unsubscribeWebPush(endpoint: string): Promise<void> {
        await this.axiosInstance.post('/notifications/push/unsubscribe', { endpoint });
    }

    // Staff Appointments APIs
    async getStaffAppointments(params?: StaffAppointmentListParams): Promise<StaffAppointmentListResponse> {
        const response = await this.axiosInstance.get('/salon-staff/appointments', { params });
        return response.data;
    }

    async getStaffAppointmentById(id: number): Promise<StaffAppointmentDetailDto> {
        const response = await this.axiosInstance.get(`/salon-staff/appointments/${id}`);
        return response.data;
    }

    async updateAppointmentStatus(id: number, status: AppointmentStatus): Promise<boolean> {
        const res = await this.axiosInstance.put(`/salon-staff/appointments/${id}/status`, { id, status });
        return res.status === 200;
    }

    async confirmAppointment(id: number): Promise<boolean> {
        const res = await this.axiosInstance.post(`/salon-staff/appointments/${id}/confirm`);
        return res.status === 200;
    }

    async cancelAppointment(id: number, reason?: string): Promise<boolean> {
        const res = await this.axiosInstance.post(`/salon-staff/appointments/${id}/cancel`, { id, reason });
        return res.status === 200;
    }

    async rescheduleAppointment(id: number, newAppointmentDate: string): Promise<boolean> {
        const res = await this.axiosInstance.put(`/salon-staff/appointments/${id}/reschedule`, { id, newAppointmentDate });
        return res.status === 200;
    }

    // Staff Services APIs
    async getStaffServices(searchTerm?: string): Promise<ServiceListResponse> {
        const response = await this.axiosInstance.get('/salon-staff/services', {
            params: searchTerm ? { searchTerm } : undefined
        });
        return response.data;
    }

    async getStaffServiceById(serviceId: number): Promise<ServiceDetailsResponse> {
        const response = await this.axiosInstance.get(`/salon-staff/services/${serviceId}`);
        return response.data;
    }

    // Owner Service APIs
    async getOwnerServices(): Promise<ServiceListResponse> {
        const response = await this.axiosInstance.get('/service');
        return response.data;
    }

    async getOwnerService(serviceId: number): Promise<ServiceDetailsResponse> {
        const response = await this.axiosInstance.get(`/service/${serviceId}`);
        return response.data;
    }

    /**
     * B7 — Bulk-clone services from a source branch to a target branch.
     * Each clone is an independent BusinessService at the target (γ-lite).
     * Services whose (category, name) already exist at the target are
     * silently skipped — BE returns both counts.
     */
    async bulkCloneServices(
        targetBranchId: number,
        body: { sourceBranchId: number; serviceIds: number[] },
    ): Promise<{ clonedCount: number; skippedCount: number; clonedServiceIds: number[]; skippedServiceNames: string[] }> {
        const response = await this.axiosInstance.post(
            `/service/branches/${targetBranchId}/bulk-clone`,
            body,
        );
        return response.data;
    }

    async createService(data: ServiceRequest): Promise<CreateServiceResponse> {
        const formData = new FormData();
        formData.append('PricingOptionsJson', JSON.stringify(data.pricingOptions));
        formData.append('MinDuration', data.minDuration);
        formData.append('Name', data.name);
        formData.append('ServiceType', data.serviceType.toString());
        formData.append('MaxDuration', data.maxDuration);
        formData.append('CategoryId', data.categoryId.toString());
        formData.append('BranchId', data.branchId.toString());
        if (data.image) formData.append('Image', data.image);
        formData.append('HasHomeService', data.hasHomeService.toString());
        if (data.description) formData.append('Description', data.description);
        if (data.staffIds && data.staffIds.length > 0) formData.append('StaffIdsJson', JSON.stringify(data.staffIds));
        // PBI 1962 (AB#1968) — send OptionGroupsJson only when the builder
        // populated it (flag ON). Absent ⇒ backend auto-wraps PricingOptionsJson
        // into an implicit Variant group (exact legacy path). Additive.
        if (data.optionGroups && data.optionGroups.length > 0) {
            formData.append('OptionGroupsJson', JSON.stringify(data.optionGroups));
        }
        const response = await this.axiosInstance.post('/service', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        return response.data;
    }

    async updateService(serviceId: number, data: UpdateServiceRequest): Promise<boolean> {
        const formData = new FormData();
        formData.append('Name', data.name);
        formData.append('BranchId', data.branchId.toString());
        // B16 — Only append CategoryId when the FE actually wants to move the
        // service. Omitting it preserves the current category (BE treats
        // null as "no change").
        if (data.categoryId && data.categoryId > 0) {
            formData.append('CategoryId', data.categoryId.toString());
        }
        formData.append('MinDuration', data.minDuration);
        formData.append('MaxDuration', data.maxDuration);
        formData.append('ServiceType', data.serviceType.toString());
        formData.append('HasHomeService', data.hasHomeService.toString());
        formData.append('IsActive', data.isActive.toString());
        formData.append('RemoveExistingImage', data.removeExistingImage.toString());
        formData.append('PricingOptionsJson', JSON.stringify(data.pricingOptions));
        if (data.image) formData.append('Image', data.image);
        if (data.description) formData.append('Description', data.description);
        if (data.staffIds && data.staffIds.length > 0) formData.append('StaffIdsJson', JSON.stringify(data.staffIds));
        // PBI 1962 (AB#1968) — additive OptionGroupsJson; see createService.
        if (data.optionGroups && data.optionGroups.length > 0) {
            formData.append('OptionGroupsJson', JSON.stringify(data.optionGroups));
        }
        const response = await this.axiosInstance.put(`/service/${serviceId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        return response.status === 200;
    }

    async deleteService(serviceId: number): Promise<boolean> {
        const response = await this.axiosInstance.delete(`/service/${serviceId}`);
        return response.status === 200;
    }

    // Staff Promotions APIs
    async getStaffPromotions(): Promise<PromotionListResponse> {
        const response = await this.axiosInstance.get('/salon-staff/promotions');
        return response.data;
    }

    async getStaffPromotionById(id: number): Promise<PromotionDto> {
        const response = await this.axiosInstance.get(`/salon-staff/promotions/${id}`);
        return response.data;
    }

    // Owner Promotions APIs
    async getOwnerPromotions(): Promise<PromotionListResponse> {
        const response = await this.axiosInstance.get('/salon-owner/promotions');
        return response.data;
    }

    async togglePromotion(id: number, data: TogglePromotionRequest): Promise<boolean> {
        const response = await this.axiosInstance.put(`/salon-owner/promotions/${id}/toggle`, { ...data, id });
        return response.data;
    }

    async deletePromotion(id: number): Promise<boolean> {
        const response = await this.axiosInstance.delete(`/salon-owner/promotions/${id}`);
        return response.data;
    }

    // ── AB#893 — Prepaid packages (owner) ────────────────────────────────
    async getOwnerPackages(): Promise<PackageListResponse> {
        const response = await this.axiosInstance.get('/salon-owner/packages');
        return response.data;
    }

    async createPackage(data: PackageRequest): Promise<number> {
        const response = await this.axiosInstance.post('/salon-owner/packages', data);
        return response.data;
    }

    async updatePackage(id: number, data: PackageRequest): Promise<boolean> {
        const response = await this.axiosInstance.put(`/salon-owner/packages/${id}`, data);
        return response.data;
    }

    async deletePackage(id: number): Promise<boolean> {
        await this.axiosInstance.delete(`/salon-owner/packages/${id}`);
        return true;
    }

    async togglePackage(id: number, isActive: boolean): Promise<boolean> {
        await this.axiosInstance.put(`/salon-owner/packages/${id}/toggle`, { isActive });
        return true;
    }

    // AB#2029 — archive (to the Archived tab) / restore (back to main list, Paused).
    async archivePackage(id: number): Promise<boolean> {
        await this.axiosInstance.put(`/salon-owner/packages/${id}/archive`);
        return true;
    }
    async restorePackage(id: number): Promise<boolean> {
        await this.axiosInstance.put(`/salon-owner/packages/${id}/restore`);
        return true;
    }

    async createDiscountedDatePromo(data: CreateDiscountedDatePromoRequest): Promise<number> {
        const response = await this.axiosInstance.post('/salon-owner/promotions/discounted-dates', data);
        return response.data;
    }

    async updateDiscountedDatePromo(id: number, data: UpdateDiscountedDatePromoRequest): Promise<boolean> {
        const response = await this.axiosInstance.put(`/salon-owner/promotions/discounted-dates/${id}`, { ...data, id });
        return response.data;
    }

    async createServiceLevelPromo(data: CreateServiceLevelPromoRequest): Promise<number> {
        const response = await this.axiosInstance.post('/salon-owner/promotions/service-level', data);
        return response.data;
    }

    async updateServiceLevelPromo(id: number, data: UpdateServiceLevelPromoRequest): Promise<boolean> {
        const response = await this.axiosInstance.put(`/salon-owner/promotions/service-level/${id}`, { ...data, id });
        return response.data;
    }

    async createCouponPromo(data: CreateCouponPromoRequest): Promise<{ promotionId: number; couponCode: string }> {
        const response = await this.axiosInstance.post('/salon-owner/promotions/coupon', data);
        return response.data;
    }

    async updateCouponPromo(id: number, data: UpdateCouponPromoRequest): Promise<boolean> {
        const response = await this.axiosInstance.put(`/salon-owner/promotions/coupon/${id}`, { ...data, id });
        return response.data;
    }

    // Staff Customer APIs
    async getCustomers(search?: string, page: number = 1, pageSize: number = 20): Promise<CustomerListResponse> {
        const response = await this.axiosInstance.get('/staff/customers', {
            params: { search, page, pageSize }
        });
        return response.data;
    }

    async getCustomerDetail(customerId: number): Promise<CustomerDetailDto> {
        const response = await this.axiosInstance.get(`/staff/customers/${customerId}`);
        return response.data;
    }

    // Owner Branches APIs
    async getBranches(): Promise<BusinessBranchListResponse> {
        const response = await this.axiosInstance.get('/salon-owner/branches');
        return response.data;
    }

    async getBranchById(id: number): Promise<BranchDto> {
        const response = await this.axiosInstance.get(`/salon-owner/branches/${id}`);
        return response.data;
    }

    async createBranch(data: CreateBranchRequest): Promise<number> {
        const response = await this.axiosInstance.post('/salon-owner/branches', data);
        return response.data;
    }

    async updateBranch(id: number, data: UpdateBranchRequest): Promise<boolean> {
        const response = await this.axiosInstance.put(`/salon-owner/branches/${id}`, data);
        return response.data;
    }

    async deleteBranch(id: number): Promise<boolean> {
        const response = await this.axiosInstance.delete(`/salon-owner/branches/${id}`);
        return response.data;
    }

    // ── AB#1747 — Per-branch working hours (Calendar v1, C1) ────────────
    /**
     * GET /salon-owner/branches/{branchId}/hours — returns 7 rows ordered
     * by DayOfWeek (0=Sun..6=Sat). Universal — any plan tier can read.
     */
    async getBranchHours(branchId: number): Promise<BranchHoursResponse> {
        const response = await this.axiosInstance.get(`/salon-owner/branches/${branchId}/hours`);
        return response.data;
    }

    /**
     * PUT /salon-owner/branches/{branchId}/hours — atomic 7-row replace.
     * Plan-gated to Growth+ via [RequireFeature(FeatureKeys.PerBranchHours)]
     * on the BE command — Starter callers receive HTTP 403 with error body
     * { code: "PLAN.PER_BRANCH_HOURS_LIMIT", ... }.
     */
    async setBranchHours(branchId: number, hours: BranchHourDto[]): Promise<BranchHoursResponse> {
        const response = await this.axiosInstance.put(
            `/salon-owner/branches/${branchId}/hours`,
            { hours },
        );
        return response.data;
    }

    // ── Epic AB#1824 — Hours single-editor (Model E) ────────────────────
    /**
     * GET /api/business/hours/effective — matrix-shaped read for the new
     * BusinessDetails > Hours single-source editor. Returns per-branch +
     * synthesized Default-row rows. See Faris brief §3.1.
     */
    async getEffectiveBranchHours(): Promise<EffectiveBranchHoursResponse> {
        const response = await this.axiosInstance.get('/business/hours/effective');
        return response.data;
    }

    /**
     * PUT /api/business/hours/bulk — transactional bulk-write across the
     * branches the owner ticked in the bulk-apply confirm modal (or one
     * branch on per-cell save). See Faris brief §3.2.
     *
     * The `Idempotency-Key` header is REQUIRED — Sara's validator rejects
     * missing values. The caller supplies the UUID v4 minted on matrix
     * mount + persisted to `sessionStorage` so retries within the same
     * session are deduplicated server-side.
     */
    async setBusinessHoursBulk(
        request: SetBusinessHoursBulkRequest,
        idempotencyKey: string,
    ): Promise<SetBusinessHoursBulkResponse> {
        const response = await this.axiosInstance.put(
            '/business/hours/bulk',
            request,
            { headers: { 'Idempotency-Key': idempotencyKey } },
        );
        return response.data;
    }

    async getDashboardSummary(startDate?: string, endDate?: string): Promise<DashboardSummaryResponse> {
        const params = new URLSearchParams();
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        const qs = params.toString();
        const response = await this.axiosInstance.get(`/salon-owner/dashboard${qs ? `?${qs}` : ''}`);
        return response.data;
    }

    // Owner Categories API (for Wizard)
    async getCategories(): Promise<CategoryListResponse> {
        const response = await this.axiosInstance.get('/category');
        return response.data;
    }

    async getBusinessCategories(): Promise<CategoryListResponse> {
        const response = await this.axiosInstance.get('/category/business');
        return response.data;
    }

    /**
     * CR-2026-05-15 — Subscribe the caller's business to an existing master
     * category. Idempotent: a repeat-subscribe returns 200 without side
     * effects. Server-side `BusinessId` comes from the JWT `businessId` claim;
     * the FE doesn't pass it in the URL or body.
     */
    async subscribeBusinessCategory(categoryId: number): Promise<boolean> {
        const response = await this.axiosInstance.post(`/category/business/${categoryId}`);
        return response.status === 200;
    }

    async removeCategoryFromBusiness(categoryId: number): Promise<boolean> {
        const response = await this.axiosInstance.delete(`/category/business/${categoryId}`);
        return response.status === 200;
    }

    // ───────────────────────────────────────────────────────────────────────
    // AB#1818 — Super Admin Category management (Policy = "SuperAdminOnly")
    // ───────────────────────────────────────────────────────────────────────

    /**
     * `GET /api/Category/admin/all` — full category list (deactivated included).
     * SuperAdmin-only on the server; non-SA callers get 403.
     *
     * AB#1872 — modes collapsed to `All | Deactivated`. When omitted the server
     * defaults to `All` (every row, deactivated included — the `archivedAt`
     * field distinguishes). The Categories page reads the full `All` list once
     * and derives the `Deactivated` tab client-side off `archivedAt`.
     */
    async getAllCategoriesAdmin(mode?: CategoryAdminListMode): Promise<CategoryListResponse> {
        const response = await this.axiosInstance.get('/category/admin/all', {
            params: mode ? { mode } : undefined,
        });
        return response.data;
    }

    /**
     * `POST /api/Category/admin/create` — multipart create. Backend honours
     * `isActive` so the "Active immediately" toggle controls publish state on
     * creation. AB#1870 — appends `targetGender` (ordinal). SuperAdmin-only.
     */
    async adminCreateCategory(data: AdminCreateCategoryRequest): Promise<RequestCategoryResponse> {
        const formData = new FormData();
        formData.append('name', data.name);
        if (data.nameAr) {
            formData.append('nameAr', data.nameAr);
        }
        formData.append('image', data.image);
        formData.append('isActive', String(data.isActive));
        if (data.targetGender != null) {
            formData.append('targetGender', String(data.targetGender));
        }
        const response = await this.axiosInstance.post('/category/admin/create', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    }

    /**
     * `PUT /api/Category/admin/{id}` — multipart edit of a master category.
     * `image` is appended ONLY when a new file is picked; omitting it preserves
     * the existing image server-side. AB#1870 — appends `targetGender`
     * (ordinal). SuperAdmin-only. Returns the updated `Category`.
     */
    async adminUpdateCategory(
        categoryId: number,
        data: AdminUpdateCategoryRequest,
    ): Promise<Category> {
        const formData = new FormData();
        formData.append('name', data.name);
        formData.append('nameAr', data.nameAr);
        if (data.image) {
            formData.append('image', data.image);
        }
        formData.append('isActive', String(data.isActive));
        if (data.targetGender != null) {
            formData.append('targetGender', String(data.targetGender));
        }
        const response = await this.axiosInstance.put(
            `/category/admin/${categoryId}`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } },
        );
        return response.data;
    }

    /**
     * `GET /api/Category/admin/{id}/impact` — pre-deactivate blast radius
     * (N salons / M services / K upcoming bookings / X single-category salons).
     * AB#1871. Call BEFORE showing the BLOCKING deactivate confirm. SuperAdmin-only.
     */
    async adminGetCategoryImpact(categoryId: number): Promise<CategoryDeactivateImpact> {
        const response = await this.axiosInstance.get(
            `/category/admin/${categoryId}/impact`,
        );
        return response.data;
    }

    /**
     * `GET /api/Category/admin/{id}/impact/salons?skip&take` — lazy drill-down
     * of affected salons (AB#1871). `take` is clamped server-side to ≤100.
     */
    async adminGetCategoryImpactSalons(
        categoryId: number,
        skip: number,
        take: number,
    ): Promise<CategoryImpactPage<CategoryImpactSalonRow>> {
        const response = await this.axiosInstance.get(
            `/category/admin/${categoryId}/impact/salons`,
            { params: { skip, take } },
        );
        return response.data;
    }

    /**
     * `GET /api/Category/admin/{id}/impact/services?skip&take` — lazy drill-down
     * of affected services (AB#1871). `take` is clamped server-side to ≤100.
     */
    async adminGetCategoryImpactServices(
        categoryId: number,
        skip: number,
        take: number,
    ): Promise<CategoryImpactPage<CategoryImpactServiceRow>> {
        const response = await this.axiosInstance.get(
            `/category/admin/${categoryId}/impact/services`,
            { params: { skip, take } },
        );
        return response.data;
    }

    /**
     * `GET /api/Category/admin/{id}/impact/bookings?skip&take` — lazy drill-down
     * of upcoming bookings (AB#1871). PDPL-SAFE: the rows carry date/time +
     * service + salon + status ONLY — no customer identity. `take` clamped ≤100.
     */
    async adminGetCategoryImpactBookings(
        categoryId: number,
        skip: number,
        take: number,
    ): Promise<CategoryImpactPage<CategoryImpactBookingRow>> {
        const response = await this.axiosInstance.get(
            `/category/admin/${categoryId}/impact/bookings`,
            { params: { skip, take } },
        );
        return response.data;
    }

    /**
     * `POST /api/Category/admin/{id}/deactivate` — soft, reversible deactivate
     * (sets `ArchivedAt`). Canonical AB#1872 route. Fresh `X-Idempotency-Key`
     * per user action. SuperAdmin-only.
     */
    async adminDeactivateCategory(categoryId: number): Promise<boolean> {
        const response = await this.axiosInstance.post(
            `/category/admin/${categoryId}/deactivate`,
            null,
            { headers: { 'X-Idempotency-Key': newIdempotencyKey() } },
        );
        return response.status >= 200 && response.status < 300;
    }

    /**
     * `POST /api/Category/admin/{id}/reactivate` — reactivate (clears
     * `ArchivedAt`). Canonical AB#1872 route. Idempotent; fresh
     * `X-Idempotency-Key` per user action. SuperAdmin-only.
     */
    async adminReactivateCategory(categoryId: number): Promise<boolean> {
        const response = await this.axiosInstance.post(
            `/category/admin/${categoryId}/reactivate`,
            null,
            { headers: { 'X-Idempotency-Key': newIdempotencyKey() } },
        );
        return response.status >= 200 && response.status < 300;
    }

    // Owner Business APIs
    async createBusiness(data: BusinessRequest): Promise<BusinessDetailsResponse> {
        const response = await this.axiosInstance.post('/business', data);
        return response.data;
    }

    async getMyBusiness(): Promise<BusinessDetailsResponse | null> {
        try {
            const response = await this.axiosInstance.get('/business/my-business');
            return response.data;
        } catch (err: unknown) {
            if ((err as { response?: { status?: number } })?.response?.status === 404) return null;
            throw err;
        }
    }

    async updateBusiness(businessId: number, data: UpdateBusinessRequest): Promise<boolean> {
        const res = await this.axiosInstance.put(`/business/${businessId}`, data);
        return res.status === 200;
    }

    async deleteBusiness(businessId: number): Promise<boolean> {
        const res = await this.axiosInstance.delete(`/business/${businessId}`);
        return res.status === 200 || res.status === 204;
    }

    // AB#1682 — BusinessTypes master picker (cached 15 min on BE).
    async getBusinessTypes(): Promise<import('../types/api-responses').BusinessTypeMasterDto[]> {
        const res = await this.axiosInstance.get('/v2/business-types');
        return res.data;
    }

    // AB#1682 — Holiday closures CRUD (tenant-scoped via JWT).
    async getMyBusinessHolidays(): Promise<import('../types/api-responses').HolidayClosureDto[]> {
        const res = await this.axiosInstance.get('/v2/business/holidays');
        return res.data;
    }

    async createBusinessHoliday(
        data: { fromDate: string; toDate: string; reason: string; source?: string }
    ): Promise<import('../types/api-responses').HolidayClosureDto> {
        const res = await this.axiosInstance.post('/v2/business/holidays', data);
        return res.data;
    }

    async updateBusinessHoliday(
        id: number,
        data: { fromDate: string; toDate: string; reason: string; source?: string }
    ): Promise<import('../types/api-responses').HolidayClosureDto> {
        const res = await this.axiosInstance.put(`/v2/business/holidays/${id}`, data);
        return res.data;
    }

    async deleteBusinessHoliday(id: number): Promise<boolean> {
        const res = await this.axiosInstance.delete(`/v2/business/holidays/${id}`);
        return res.status === 200 || res.status === 204;
    }

    // Staff Invitation APIs
    async validateInvitationToken(token: string): Promise<InvitationDetailsResponse> {
        const response = await this.axiosInstance.get(`/staff-invitation/validate?token=${encodeURIComponent(token)}`);
        return response.data;
    }

    async acceptInvitation(data: AcceptInvitationRequest): Promise<AcceptInvitationResponse> {
        const response = await this.axiosInstance.post('/staff-invitation/accept', data);
        return response.data;
    }

    async getBusinessStaff(params?: StaffListParams): Promise<BusinessStaffListResponse> {
        const response = await this.axiosInstance.get('/salon-owner/staff', { params });
        // AB#2002 — coerce string enum-name schedules[].dayOfWeek → 0..6 int so
        // EmployeeModal's getDayName(dayOfWeek) resolves (was rendering blank
        // "Work Schedule" day labels when editing an existing staff member).
        return normalizeBusinessStaffList(response.data);
    }

    async registerStaff(data: RegisterStaffRequest): Promise<number> {
        const response = await this.axiosInstance.post('/salon-owner/staff/register', data);
        return response.data;
    }

    /** Batch B (AB#2176) — creates the staff record and returns its real staff id. */
    async createStaffRecord(data: CreateStaffRecordRequest): Promise<number> {
        const response = await this.axiosInstance.post('/salon-owner/staff', data);
        return response.data;
    }

    /** Batch B (AB#2176) — invites an existing staff record to the portal; returns the invitation id. */
    async inviteStaffRecord(staffId: number, data: InviteStaffRecordRequest): Promise<number> {
        const response = await this.axiosInstance.post(`/salon-owner/staff/${staffId}/invite`, data);
        return response.data;
    }

    async updateStaff(staffId: number, data: UpdateStaffRequest): Promise<boolean> {
        const response = await this.axiosInstance.put(`/salon-owner/staff/${staffId}`, data);
        return response.data;
    }

    async deleteStaff(staffId: number): Promise<boolean> {
        const response = await this.axiosInstance.delete(`/salon-owner/staff/${staffId}`);
        return response.data;
    }

    // CR-2026-05-15-v3 — replace the capability set (which services this staff
    // can perform) atomically. Idempotent: sending the same list twice is a
    // no-op. Empty list clears all capability links for this staff.
    //
    // AB#1862 — `forceWithActiveBookings` is the opt-in override flag the
    // owner sends after acknowledging the booking-conflict confirm dialog.
    // Omitted / false → the BE pre-flights and returns 422 +
    // `STAFF_REASSIGN_HAS_ACTIVE_BOOKINGS` if removing a service would orphan
    // future appointments. True → the BE proceeds anyway and logs the
    // override (Serilog `staff.reassign.forced_with_orphans`).
    async setStaffServices(
        staffId: number,
        serviceIds: number[],
        forceWithActiveBookings: boolean = false,
    ): Promise<boolean> {
        const body: { serviceIds: number[]; forceWithActiveBookings?: boolean } = { serviceIds };
        if (forceWithActiveBookings) body.forceWithActiveBookings = true;
        const response = await this.axiosInstance.put(
            `/salon-owner/staff/${staffId}/services`,
            body,
        );
        return response.status >= 200 && response.status < 300;
    }

    // CR-2026-05-16-v1 — Pending invitations surface.
    async getPendingInvitations(): Promise<import('../types/api-responses').PendingInvitationDto[]> {
        const response = await this.axiosInstance.get('/salon-owner/staff/invitations');
        return response.data ?? [];
    }

    async resendInvitation(invitationId: number): Promise<boolean> {
        const response = await this.axiosInstance.post(`/salon-owner/staff/invitations/${invitationId}/resend`);
        return response.status >= 200 && response.status < 300;
    }

    async revokeInvitation(invitationId: number): Promise<boolean> {
        const response = await this.axiosInstance.delete(`/salon-owner/staff/invitations/${invitationId}`);
        return response.status >= 200 && response.status < 300;
    }

    /* ──────────────────────────────────────────────────────────────────
     * AB#1842 (PBI-F) — Salon-gender hard-block: override endpoints.
     *
     * The validator on the BE rejects mismatched gender at three points
     * (invite, ServiceType flip, invitation accept). Owners with a
     * genuine exception (e.g. a non-customer-facing delivery staff)
     * submit an override request that a Super Admin reviews. The owner
     * route accepts only Pending creates; the admin route accepts
     * Approved / Rejected decisions and queries.
     * ───────────────────────────────────────────────────────────────── */

    /** Owner: submit a gender-mismatch override request for one specific
     *  staff member. The BE creates the row with `Status="Pending"` and
     *  fans out a notification event to the Super Admin queue.
     *
     *  See Faris brief §3 on AB#1842.
     */
    async requestGenderOverride(
        businessId: number,
        staffId: number,
        data: RequestGenderOverrideRequest,
    ): Promise<{ overrideId: number; status: GenderOverrideStatus; createdAt: string }> {
        const response = await this.axiosInstance.post(
            `/super-admin/businesses/${businessId}/staff/${staffId}/gender-mismatch-override`,
            data,
        );
        return response.data;
    }

    /** Super Admin: list pending or historical overrides. `status` filters
     *  the list (default = Pending = the queue). Omitting the filter returns
     *  the historical (Approved + Rejected) audit log.
     */
    async listGenderOverrides(
        status: GenderOverrideStatus | 'AllDecided' = 'Pending',
    ): Promise<GenderOverrideListItem[]> {
        const response = await this.axiosInstance.get(
            '/super-admin/gender-mismatch-overrides',
            { params: { status } },
        );
        return response.data ?? [];
    }

    /** Super Admin: approve or reject a pending override. Mutates `Status`
     *  and writes an audit row (`GenderMismatchOverrideAudit`) via the
     *  Faris-spec'd append-only history table.
     */
    async decideGenderOverride(
        overrideId: number,
        data: DecideGenderOverrideRequest,
    ): Promise<GenderOverrideListItem> {
        const response = await this.axiosInstance.patch(
            `/super-admin/gender-mismatch-overrides/${overrideId}`,
            data,
        );
        return response.data;
    }

    // CR-2026-05-16-v2 — Replace branch enrollment for a staff atomically.
    // Idempotent; empty array clears (= implicit-all).
    //
    // AB#1862 — `forceWithActiveBookings` is the owner-acknowledged override
    // for the booking-conflict guard. See `setStaffServices` doc — same
    // semantics, branch-axis instead of service-axis.
    async setStaffBranches(
        staffId: number,
        branchIds: number[],
        forceWithActiveBookings: boolean = false,
    ): Promise<boolean> {
        const body: { branchIds: number[]; forceWithActiveBookings?: boolean } = { branchIds };
        if (forceWithActiveBookings) body.forceWithActiveBookings = true;
        const response = await this.axiosInstance.put(
            `/salon-owner/staff/${staffId}/branches`,
            body,
        );
        return response.status >= 200 && response.status < 300;
    }

    /**
     * AB#1716 (CR-2026-05-17b) — Replace the per-branch schedule for a staff
     * member atomically. Idempotent. Empty `slots` clears the schedule.
     *
     * For multi-branch businesses, `branchId` is required on every slot
     * (server-enforced). For single-branch businesses it may be null —
     * the server auto-fills with the sole branch.
     */
    async setStaffSchedule(
        staffId: number,
        slots: import('../types/api-responses').ScheduleSlotPayload[],
    ): Promise<boolean> {
        const response = await this.axiosInstance.put(
            `/salon-owner/staff/${staffId}/schedule`,
            { slots },
        );
        return response.status >= 200 && response.status < 300;
    }

    /**
     * AB#1716 (CR-2026-05-17b) — Read the per-branch schedule for a staff
     * member. Returns an empty slots array when no hours are set yet.
     */
    async getStaffSchedule(
        staffId: number,
    ): Promise<import('../types/api-responses').StaffScheduleResponse> {
        const response = await this.axiosInstance.get(
            `/salon-owner/staff/${staffId}/schedule`,
        );
        return response.data ?? { staffId, slots: [] };
    }

    // ── AB#1976 (CR-2026-07-04) — Staff absence management ───────────────

    /** List all live absences for a staff member (owner-facing). */
    async getStaffAbsences(
        staffId: number,
    ): Promise<import('../types/api-responses').StaffAbsencesResponse> {
        const response = await this.axiosInstance.get(
            `/salon-owner/staff/${staffId}/absences`,
        );
        return response.data ?? { staffId, absences: [] };
    }

    /** Create a new absence. Returns the saved row + any non-blocking warnings. */
    async createStaffAbsence(
        staffId: number,
        payload: import('../types/api-responses').StaffAbsencePayload,
    ): Promise<import('../types/api-responses').StaffAbsenceResponse> {
        const response = await this.axiosInstance.post(
            `/salon-owner/staff/${staffId}/absences`,
            payload,
        );
        return response.data;
    }

    /** AB#2021 — edit an existing absence (PUT upserts by absenceId). */
    async updateStaffAbsence(
        staffId: number,
        absenceId: number,
        payload: import('../types/api-responses').StaffAbsencePayload,
    ): Promise<import('../types/api-responses').StaffAbsenceResponse> {
        const response = await this.axiosInstance.put(
            `/salon-owner/staff/${staffId}/absences/${absenceId}`,
            payload,
        );
        return response.data;
    }

    /** AB#2024 — the per-branch break policy (caps + override), or defaults. */
    async getBranchAbsencePolicy(
        branchId: number,
    ): Promise<import('../types/api-responses').BranchAbsencePolicyDto> {
        const response = await this.axiosInstance.get(
            `/salon-owner/branches/${branchId}/absence-policy`,
        );
        return response.data;
    }

    /** AB#2024 — update the per-branch break policy. */
    async updateBranchAbsencePolicy(
        branchId: number,
        payload: import('../types/api-responses').BranchAbsencePolicyDto,
    ): Promise<import('../types/api-responses').BranchAbsencePolicyDto> {
        const response = await this.axiosInstance.put(
            `/salon-owner/branches/${branchId}/absence-policy`,
            payload,
        );
        return response.data;
    }

    /** Soft-delete an absence. */
    async deleteStaffAbsence(staffId: number, absenceId: number): Promise<boolean> {
        const response = await this.axiosInstance.delete(
            `/salon-owner/staff/${staffId}/absences/${absenceId}`,
        );
        return response.status >= 200 && response.status < 300;
    }

    // CR-2026-05-16-v1 — Staff photo upload. Multipart; field name "photo".
    // Returns the relative storage path that the BE saved into User.ProfilePictureUrl.
    //
    // CRITICAL: do NOT set Content-Type manually. When a FormData body is
    // passed, axios/the browser auto-sets `Content-Type: multipart/form-data;
    // boundary=<random>`. Setting it explicitly to `multipart/form-data`
    // CLOBBERS the auto-detected header — the request goes out without the
    // boundary parameter, the server can't parse the body, and the upload
    // silently fails. Original CR-v1 had this bug and produced "Failed to
    // save photo" on the founder's UAT.
    async setStaffPhoto(staffId: number, photo: File): Promise<string> {
        const formData = new FormData();
        formData.append('photo', photo);
        const response = await this.axiosInstance.put(
            `/salon-owner/staff/${staffId}/photo`,
            formData,
        );
        return response.data?.photoUrl ?? '';
    }

    async createAppointment(data: AppointmentRequest): Promise<AppointmentResponse> {
        const response = await this.axiosInstance.post('/salon-owner/appointments', data);
        return response.data;
    }

    async getOwnerAppointments(params?: OwnerAppointmentListParams): Promise<OwnerAppointmentListResponse> {
        const queryParams = new URLSearchParams();
        if (params?.startDate) queryParams.append('startDate', params.startDate);
        if (params?.endDate) queryParams.append('endDate', params.endDate);
        if (params?.branchId !== undefined) queryParams.append('branchId', params.branchId.toString());
        if (params?.paymentStatus !== undefined) queryParams.append('paymentStatus', params.paymentStatus.toString());
        if (params?.categoryId !== undefined) queryParams.append('categoryId', params.categoryId.toString());
        if (params?.page) queryParams.append('page', params.page.toString());
        if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
        const qs = queryParams.toString();
        const response = await this.axiosInstance.get(`/salon-owner/appointments${qs ? `?${qs}` : ''}`);
        return response.data;
    }

    async getAppointmentById(id: number): Promise<AppointmentResponse> {
        const response = await this.axiosInstance.get(`/salon-owner/appointments/${id}`);
        return response.data;
    }

    async updateAppointment(id: number, data: Omit<AppointmentRequest, 'businessId'>): Promise<boolean> {
        const res = await this.axiosInstance.put(`/salon-owner/appointments/${id}`, { id, ...data });
        return res.status === 200;
    }

    async deleteAppointment(id: number): Promise<boolean> {
        const res = await this.axiosInstance.delete(`/salon-owner/appointments/${id}`);
        return res.status === 200;
    }

    async rescheduleOwnerAppointment(id: number, newAppointmentDate: string): Promise<boolean> {
        const res = await this.axiosInstance.put(`/salon-owner/appointments/${id}/reschedule`, { newAppointmentDate });
        return res.status === 200;
    }

    /**
     * AB#2109 (Admin V1 P0-3) — owner calendar status transition through the
     * owner state-machine endpoint (AB#1750). The server validates the
     * transition (and the business's disabled statuses) and notifies the
     * customer when the booking is marked Completed. Owner sessions must NOT use
     * the staff `updateAppointmentStatus`: that endpoint resolves a SalonStaff
     * row by the caller's profile, which an owner never has (→ 500).
     * `to` is sent as the numeric enum value — FE and BE share 1..7 (the names
     * differ only at 2: FE Confirmed / BE Approved).
     */
    async changeOwnerAppointmentStatus(id: number, to: AppointmentStatus): Promise<boolean> {
        const res = await this.axiosInstance.post(`/salon-owner/appointments/${id}/status`, { to });
        return res.status >= 200 && res.status < 300;
    }

    // Support Tickets
    async getSupportCategories(): Promise<{ categories: Array<{ id: string; name: string; description?: string }> }> {
        const res = await this.axiosInstance.get('/support/categories');
        return res.data;
    }

    async createSupportTicket(data: { category: string; description: string; email?: string; fullName?: string; preferredContactMethod?: string }): Promise<{ ticketId: number; ticketNumber: string; message: string }> {
        const res = await this.axiosInstance.post('/support/tickets', data);
        return res.data;
    }

    async getSupportTickets(status?: string, page = 1, pageSize = 20): Promise<{ tickets: Array<any>; totalCount: number; page: number; pageSize: number }> {
        const params = new URLSearchParams();
        if (status) params.append('status', status);
        params.append('page', page.toString());
        params.append('pageSize', pageSize.toString());
        const res = await this.axiosInstance.get(`/support/tickets?${params.toString()}`);
        return res.data;
    }

    async getSupportTicketById(id: number): Promise<any> {
        const res = await this.axiosInstance.get(`/support/tickets/${id}`);
        return res.data;
    }

    async replyToSupportTicket(id: number, message: string): Promise<boolean> {
        const res = await this.axiosInstance.post(`/support/tickets/${id}/reply`, { message });
        return res.status === 200;
    }

    async closeSupportTicket(id: number): Promise<boolean> {
        const res = await this.axiosInstance.put(`/support/tickets/${id}/close`);
        return res.status === 200;
    }

    // Super Admin Dashboard
    async getSuperAdminDashboard(startDate?: string, endDate?: string, include?: string): Promise<SuperAdminDashboardResponse> {
        const params = new URLSearchParams();
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        if (include) params.append('include', include);
        const res = await this.axiosInstance.get<SuperAdminDashboardResponse>(`/super-admin/dashboard?${params.toString()}`);
        return res.data;
    }

    // Super Admin — Sponsored Placement CMS (AB#1732 / FU-5).
    // Backend admin endpoint tracked on AB#1745. Until that ships, the service
    // layer wrapper (`sponsoredPlacementService`) delegates to an in-memory
    // mock; these methods are wired now so the cutover is env-flag-only.
    async listSponsoredBusinesses(params: import('../types/sponsoredPlacement').SponsoredListParams): Promise<import('../types/sponsoredPlacement').SponsoredListResponse> {
        const query: Record<string, string | number> = {};
        if (params.search) query.search = params.search;
        if (params.city) query.city = params.city;
        if (params.tier !== undefined && params.tier !== null) query.tier = params.tier;
        if (params.sponsoredFilter) query.sponsoredFilter = params.sponsoredFilter;
        query.page = params.page ?? 1;
        query.pageSize = params.pageSize ?? 10;
        const res = await this.axiosInstance.get<import('../types/sponsoredPlacement').SponsoredListResponse>('/super-admin/businesses', { params: query });
        return res.data;
    }

    async setSponsoredPlacement(
        businessId: number,
        body: import('../types/sponsoredPlacement').SetSponsoredPlacementRequest,
    ): Promise<import('../types/sponsoredPlacement').SponsoredBusinessDto> {
        const res = await this.axiosInstance.put<import('../types/sponsoredPlacement').SponsoredBusinessDto>(
            `/super-admin/businesses/${businessId}/sponsored-placement`,
            body,
        );
        return res.data;
    }

    async clearSponsoredPlacement(businessId: number): Promise<import('../types/sponsoredPlacement').SponsoredBusinessDto> {
        const res = await this.axiosInstance.delete<import('../types/sponsoredPlacement').SponsoredBusinessDto>(
            `/super-admin/businesses/${businessId}/sponsored-placement`,
        );
        return res.data;
    }

    async clearExpiredSponsoredPlacements(): Promise<import('../types/sponsoredPlacement').BulkClearExpiredResponse> {
        const res = await this.axiosInstance.post<import('../types/sponsoredPlacement').BulkClearExpiredResponse>(
            '/super-admin/businesses/sponsored-placement/clear-expired',
        );
        return res.data;
    }

    async bulkSetSponsoredPriority(
        body: import('../types/sponsoredPlacement').BulkPriorityRequest,
    ): Promise<import('../types/sponsoredPlacement').BulkPriorityResponse> {
        const res = await this.axiosInstance.post<import('../types/sponsoredPlacement').BulkPriorityResponse>(
            '/super-admin/businesses/sponsored-placement/bulk-priority',
            body,
        );
        return res.data;
    }

    // ── AB#1749 — Calendar grid (C3 of Calendar v1 Epic AB#1746) ─────────
    // Read-only one-shot payload for the owner day-view calendar.
    async getCalendarGrid(params: { branchId: number; date: string }):
        Promise<import('../types/api-responses').CalendarGridResponse> {
        const res = await this.axiosInstance.get<import('../types/api-responses').CalendarGridResponse>(
            '/salon-owner/calendar/grid',
            { params: { branchId: params.branchId, date: params.date } },
        );
        return res.data;
    }

    // ── AB#1751 — Calendar availability + book (C5 of Calendar v1) ──────
    // Backs the service-first quick-book modal: an availability search
    // returns the next N slots; the book endpoint creates the appointment
    // idempotently (Idempotency-Key header required).
    async getCalendarAvailability(
        params: import('../types/api-responses').CalendarAvailabilityParams,
    ): Promise<import('../types/api-responses').CalendarAvailabilityResponse> {
        const queryParams: Record<string, string | number> = {
            branchId: params.branchId,
            serviceId: params.serviceId,
            from: params.from,
            take: params.take ?? 15,
        };
        if (params.around) queryParams.around = params.around;
        if (params.staffId) queryParams.staffId = params.staffId;
        if (params.staffGender) queryParams.staffGender = params.staffGender;

        const res = await this.axiosInstance.get<import('../types/api-responses').CalendarAvailabilityResponse>(
            '/salon-owner/calendar/availability',
            { params: queryParams },
        );
        return res.data;
    }

    async bookAppointment(
        body: import('../types/api-responses').BookAppointmentRequest,
        idempotencyKey: string,
    ): Promise<import('../types/api-responses').BookAppointmentResponse> {
        const res = await this.axiosInstance.post<import('../types/api-responses').BookAppointmentResponse>(
            '/salon-owner/calendar/book',
            body,
            { headers: { 'Idempotency-Key': idempotencyKey } },
        );
        return res.data;
    }

    // ── AB#1754 — Calendar settings (C8 of Calendar v1 Epic AB#1746) ─────
    // BE exposes a single atomic endpoint at `/api/calendar/settings`
    // (NOT `/salon-owner/calendar-settings` as the v0 spec described).
    // The PUT body replaces both the settings row and all 7 status rows in
    // one transaction — there is NO per-status PUT.
    async getCalendarSettings(): Promise<import('../types/api-responses').CalendarSettingsResponse> {
        const res = await this.axiosInstance.get<import('../types/api-responses').CalendarSettingsResponse>(
            '/calendar/settings',
        );
        return res.data;
    }

    async updateCalendarSettings(
        body: import('../types/api-responses').UpdateCalendarSettingsRequest,
    ): Promise<import('../types/api-responses').CalendarSettingsResponse> {
        const res = await this.axiosInstance.put<import('../types/api-responses').CalendarSettingsResponse>(
            '/calendar/settings',
            body,
        );
        return res.data;
    }

    // Phase 5 — Owner Payments
    async getOwnerTransactions(page = 1, pageSize = 20, filters?: { status?: string; startDate?: string; endDate?: string }): Promise<TransactionListResponse> {
        const params: Record<string, string | number> = { page, pageSize };
        if (filters?.status) params.status = filters.status;
        if (filters?.startDate) params.startDate = filters.startDate;
        if (filters?.endDate) params.endDate = filters.endDate;
        const res = await this.axiosInstance.get('/salon-owner/payments/transactions', { params });
        // AB#2060 — the backend PaymentHistoryResponse serializes as { payments, totalCount }
        // with each row carrying `createdAt`; the FE expects { transactions, totalCount, ... }
        // with `date`. Adapt the field names and guard so the pay-at-salon Payments page never
        // crashes on `.map`/`.reduce` of undefined (this mismatch was the root of the
        // "Something went wrong loading Payments" crash).
        const d = (res.data ?? {}) as { transactions?: TransactionDto[]; payments?: TransactionDto[]; totalCount?: number; totalRevenue?: number };
        const raw = (d.transactions ?? d.payments ?? []) as Array<TransactionDto & { createdAt?: string }>;
        const transactions: TransactionDto[] = raw.map((p) => ({ ...p, date: p.date ?? p.createdAt ?? '' }));
        // Leave totalRevenue undefined when the backend omits it so the page computes it from
        // the CAPTURED transactions (AB#2060) rather than showing 0.
        return { transactions, totalCount: d.totalCount ?? transactions.length, totalRevenue: d.totalRevenue, page, pageSize };
    }

    async getAppointmentPaymentDetails(appointmentId: number): Promise<AppointmentPaymentDetails | null> {
        try {
            const res = await this.axiosInstance.get(`/salon-owner/payments/appointment/${appointmentId}`);
            return res.data;
        } catch (err: unknown) {
            if ((err as { response?: { status?: number } })?.response?.status === 404) return null;
            throw err;
        }
    }

    async getOwnerCommissions(page = 1, pageSize = 20): Promise<CommissionListResponse> {
        const res = await this.axiosInstance.get('/salon-owner/payments/commissions', { params: { page, pageSize } });
        return res.data;
    }

    async getOwnerPayouts(page = 1, pageSize = 20): Promise<PayoutListResponse> {
        const res = await this.axiosInstance.get('/salon-owner/payments/payouts', { params: { page, pageSize } });
        return res.data;
    }

    async updatePayoutIban(iban: string): Promise<boolean> {
        const res = await this.axiosInstance.put('/salon-owner/payments/payout-iban', { iban });
        return res.status === 200;
    }

    // Stripe — Payment Intents
    async createPaymentIntent(amount: number, currency = 'usd', metadata?: Record<string, string>): Promise<StripePaymentIntentResponse> {
        const res = await this.axiosInstance.post('/payments/create-payment-intent', { amount, currency, metadata });
        return res.data;
    }

    async confirmPaymentIntent(paymentIntentId: string): Promise<{ status: string }> {
        const res = await this.axiosInstance.post('/payments/confirm-payment-intent', { paymentIntentId });
        return res.data;
    }

    async getSavedPaymentMethods(): Promise<StripePaymentMethodDto[]> {
        const res = await this.axiosInstance.get('/payments/payment-methods');
        return res.data;
    }

    async detachPaymentMethod(paymentMethodId: string): Promise<boolean> {
        const res = await this.axiosInstance.delete(`/payments/payment-methods/${paymentMethodId}`);
        return res.status === 200;
    }

    // Phase 6 — Subscription & No-Show Policy
    // BE controller route is `[Route("api/subscriptions")]` (PLURAL). The FE
    // had been calling `/subscription/...` (singular) for getPlans /
    // getCurrentSubscription / subscribeToPlan / getInvoices, which 404'd
    // silently. Surfaced during AB#1638 Wave 1 UAT 2026-05-03 — Step 5's
    // plan picker stayed empty because /api/subscription/plans was 404 and
    // the FE silently fell back to errors.generic.
    async getPlans(): Promise<PlanDto[]> {
        const res = await this.axiosInstance.get('/subscriptions/plans');
        return res.data;
    }

    async getCurrentSubscription(): Promise<SubscriptionDto | null> {
        try {
            const res = await this.axiosInstance.get('/subscriptions/current');
            return res.data;
        } catch (err: unknown) {
            if ((err as { response?: { status?: number } })?.response?.status === 404) return null;
            throw err;
        }
    }

    async subscribeToPlan(planId: number): Promise<{ subscriptionId: number }> {
        const res = await this.axiosInstance.post('/subscriptions/subscribe', { planId });
        return res.data;
    }

    /**
     * AB#1904 (W-1) — Mint a Stripe-hosted Checkout Session for a paid
     * upgrade. BE returns `{ url, sessionId }`. The FE redirects via
     * `window.location.href = url` so the owner lands on Stripe's hosted
     * payment page. After payment Stripe redirects back to
     * `/subscription/success?session_id=…` or `/subscription/cancelled`.
     *
     * Optional `Idempotency-Key` header — caller mints a UUID v4 once per
     * UpgradeConfirmModal open and re-uses it across retries so an
     * accidental double-click doesn't create two Checkout Sessions.
     */
    async createCheckoutSession(
        request: CreateCheckoutSessionRequest,
        idempotencyKey?: string,
    ): Promise<CreateCheckoutSessionResponse> {
        const res = await this.axiosInstance.post(
            '/subscriptions/checkout-session',
            request,
            idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : undefined,
        );
        return res.data;
    }

    /**
     * Creates a Stripe trial subscription (PBI-C, AB#1464). Used by the
     * onboarding wizard Payment step. Returns a pending_setup_intent
     * client_secret that the FE confirms with Stripe Elements to collect a
     * card. On confirmation, a webhook transitions the subscription from
     * Incomplete → Trial (no charge during trial).
     */
    async activateTrial(
        request: ActivateTrialRequest,
    ): Promise<TrialActivationResponse> {
        const res = await this.axiosInstance.post('/subscriptions/activate-trial', request);
        return res.data;
    }

    async getInvoices(page = 1, pageSize = 20): Promise<InvoiceListResponse> {
        const res = await this.axiosInstance.get('/subscriptions/invoices', { params: { page, pageSize } });
        return res.data;
    }

    async getNoShowPolicy(): Promise<NoShowPolicyDto> {
        const res = await this.axiosInstance.get('/salon-owner/settings/no-show-policy');
        return res.data;
    }

    async updateNoShowPolicy(data: NoShowPolicyDto): Promise<boolean> {
        const res = await this.axiosInstance.put('/salon-owner/settings/no-show-policy', data);
        return res.status === 200;
    }

    // Phase 7 — Super Admin: Users, Owners, Feature Flags, Audit Log
    // Backend returns { items, totalCount, page, pageSize, totalPages } with server-side field names
    // (userId/isActive/createdAt). We normalize to the frontend DTO shape here so page components
    // can stay agnostic to backend naming.
    async getAdminUsers(params?: { search?: string; role?: string; page?: number; pageSize?: number }): Promise<AdminUserListResponse> {
        const res = await this.axiosInstance.get<{
            items?: Array<{ userId: number; fullName: string; email: string; role: string; isActive: boolean; createdAt: string }>;
            totalCount?: number;
            page?: number;
            pageSize?: number;
        }>('/super-admin/users', { params });
        const body = res.data ?? {};
        const items = Array.isArray(body.items) ? body.items : [];
        return {
            users: items.map(i => ({
                id: i.userId,
                fullName: i.fullName ?? '',
                email: i.email ?? '',
                role: i.role ?? '',
                status: i.isActive ? 'Active' : 'Inactive',
                lastLogin: i.createdAt ?? ''
            })),
            totalCount: body.totalCount ?? 0,
            page: body.page ?? (params?.page ?? 1),
            pageSize: body.pageSize ?? (params?.pageSize ?? 10)
        };
    }

    /**
     * AB#2112 — set the account's state explicitly (never a blind flip; an
     * empty body used to be read as "deactivate"). Deactivation signs the
     * account out everywhere; `changed: false` means it already had that state.
     */
    async toggleAdminUserStatus(userId: number, isActive: boolean): Promise<{ changed: boolean }> {
        const res = await this.axiosInstance.put<{ changed?: boolean }>(
            `/super-admin/users/${userId}/toggle-status`,
            { isActive },
        );
        return { changed: res.data?.changed !== false };
    }

    async getAdminOwners(params?: { search?: string; page?: number; pageSize?: number }): Promise<AdminOwnerListResponse> {
        const res = await this.axiosInstance.get<{
            items?: Array<{
                userId: number;
                fullName: string;
                email: string;
                isActive: boolean;
                businesses?: Array<{ businessId?: number; name?: string; status?: string; suspensionReason?: string | null }>;
            }>;
            totalCount?: number;
            page?: number;
            pageSize?: number;
        }>('/super-admin/owners', { params });
        const body = res.data ?? {};
        const items = Array.isArray(body.items) ? body.items : [];
        return {
            owners: items.map(i => {
                const businesses = (Array.isArray(i.businesses) ? i.businesses : [])
                    .filter((b): b is { businessId: number; name?: string; status?: string; suspensionReason?: string | null } =>
                        typeof b?.businessId === 'number')
                    .map(b => ({
                        businessId: b.businessId,
                        name: b.name ?? '—',
                        status: b.status ?? '',
                        suspensionReason: b.suspensionReason ?? null,
                    }));
                return {
                    id: i.userId,
                    fullName: i.fullName ?? '',
                    email: i.email ?? '',
                    businessName: businesses[0]?.name || '—',
                    status: i.isActive ? 'Active' : 'Inactive',
                    businesses,
                };
            }),
            totalCount: body.totalCount ?? 0,
            page: body.page ?? (params?.page ?? 1),
            pageSize: body.pageSize ?? (params?.pageSize ?? 10)
        };
    }

    /** AB#2112 — as {@link toggleAdminUserStatus}; the owner's salon is not affected. */
    async toggleAdminOwnerStatus(ownerId: number, isActive: boolean): Promise<{ changed: boolean }> {
        const res = await this.axiosInstance.put<{ changed?: boolean }>(
            `/super-admin/owners/${ownerId}/toggle-status`,
            { isActive },
        );
        return { changed: res.data?.changed !== false };
    }

    /**
     * AB#2045 — SuperAdmin grant of a complimentary (non-Stripe) Trial
     * subscription to a business so it can be provisioned as founding/free and
     * add more than one branch. Wraps
     * `POST /api/super-admin/businesses/{businessId}/grant-subscription`
     * (SuperAdminSubscriptionController; SuperAdmin-only, re-authorized server
     * side). Body is optional — the backend defaults to Enterprise tier + a
     * 365-day comp window. `planTier` is sent as the enum NAME because the API
     * serializes enums as strings (JsonStringEnumConverter). Idempotent: a prior
     * comp grant returns 200 with `alreadyGranted = true`; a real paid
     * subscription is rejected 409 (Subscription.CONFLICT).
     */
    async grantSubscription(
        businessId: number,
        payload?: GrantSubscriptionRequest,
    ): Promise<GrantSubscriptionResponse> {
        const res = await this.axiosInstance.post<GrantSubscriptionResponse>(
            `/super-admin/businesses/${businessId}/grant-subscription`,
            payload ?? {},
        );
        return res.data;
    }

    /**
     * P0-7 (AB#2047) — SuperAdmin read-only "locate a customer's booking"
     * lookup for pilot support. Wraps
     * `GET /api/super-admin/businesses/{businessId}/appointments`
     * (SuperAdmin-only, re-authorized server side). Returns a paged list of
     * de-identified appointments (name + phone only — no email/other PII).
     * Strictly read-only: there is no matching mutation.
     */
    async getBusinessAppointments(
        businessId: number,
        params?: GetBusinessAppointmentsParams,
    ): Promise<AdminAppointmentListResponse> {
        const res = await this.axiosInstance.get<{
            items?: Array<Partial<AdminAppointmentLocateDto>>;
            totalCount?: number;
            pageNumber?: number;
            pageSize?: number;
        }>(`/super-admin/businesses/${businessId}/appointments`, { params });
        const body = res.data ?? {};
        const items = Array.isArray(body.items) ? body.items : [];
        return {
            items: items.map(a => ({
                appointmentId: a.appointmentId ?? 0,
                appointmentDate: a.appointmentDate ?? '',
                durationMinutes: typeof a.durationMinutes === 'number' ? a.durationMinutes : 0,
                status: a.status ?? '',
                serviceName: a.serviceName ?? '',
                staffName: a.staffName ?? null,
                customerName: a.customerName ?? '',
                customerPhone: a.customerPhone ?? null,
                amount: typeof a.amount === 'number' ? a.amount : 0,
            })),
            totalCount: body.totalCount ?? 0,
            pageNumber: body.pageNumber ?? (params?.pageNumber ?? 1),
            pageSize: body.pageSize ?? (params?.pageSize ?? 10),
        };
    }

    /**
     * Batch C (AB#2195) — Super Admin booking search across salons, by booking
     * number or customer phone (any format; the server normalises it). Wraps
     * `GET /api/super-admin/appointments/search` (SuperAdmin-only, audited).
     */
    async searchAdminAppointments(params: SearchAdminAppointmentsParams): Promise<AdminSupportAppointmentListResponse> {
        const res = await this.axiosInstance.get<{
            items?: Array<Partial<AdminSupportAppointmentDto>>;
            totalCount?: number;
            page?: number;
            pageSize?: number;
        }>('/super-admin/appointments/search', { params });
        const body = res.data ?? {};
        return {
            items: (Array.isArray(body.items) ? body.items : []).map(toAdminSupportAppointment),
            totalCount: body.totalCount ?? 0,
            pageNumber: body.page ?? (params.pageNumber ?? 1),
            pageSize: body.pageSize ?? (params.pageSize ?? 20),
        };
    }

    /**
     * Batch C (AB#2195) — Super Admin cancels a pending or confirmed booking, with
     * a reason. The customer and the salon owner are notified by the server.
     */
    async cancelAppointmentAsAdmin(appointmentId: number, reason: string): Promise<void> {
        await this.axiosInstance.post(`/super-admin/appointments/${appointmentId}/cancel`, { reason });
    }

    /**
     * Batch C (AB#2195) — Super Admin suspends an approved salon, with a reason.
     * Returns the salon's upcoming open bookings, which are NOT cancelled.
     */
    async suspendBusiness(businessId: number, reason: string): Promise<BusinessSuspensionResult> {
        const res = await this.axiosInstance.post<{
            status?: string;
            openFutureBookings?: Array<Partial<AdminSupportAppointmentDto>>;
        }>(`/super-admin/businesses/${businessId}/suspend`, { reason });
        return {
            status: res.data?.status ?? 'Suspended',
            openFutureBookings: (res.data?.openFutureBookings ?? []).map(toAdminSupportAppointment),
        };
    }

    /** Batch C (AB#2195) — Super Admin reinstates a suspended salon. */
    async reinstateBusiness(businessId: number, reason?: string): Promise<void> {
        await this.axiosInstance.post(`/super-admin/businesses/${businessId}/reinstate`, { reason: reason || null });
    }

    /** Batch C (AB#2195) — a salon's upcoming open (pending / confirmed) bookings. */
    async getOpenBookingsForAdmin(businessId: number): Promise<AdminSupportAppointmentDto[]> {
        const res = await this.axiosInstance.get<Array<Partial<AdminSupportAppointmentDto>>>(
            `/super-admin/businesses/${businessId}/open-bookings`,
        );
        return (Array.isArray(res.data) ? res.data : []).map(toAdminSupportAppointment);
    }

    async getFeatureFlags(): Promise<FeatureFlagDto[]> {
        const res = await this.axiosInstance.get<Array<{
            id: number; key: string; name: string; isEnabled: boolean; rolloutPercentage: number;
        }>>('/super-admin/feature-flags');
        const body = Array.isArray(res.data) ? res.data : [];
        return body.map(f => ({
            id: f.id,
            key: f.key ?? '',
            name: f.name ?? '',
            enabled: Boolean(f.isEnabled),
            rolloutPercent: typeof f.rolloutPercentage === 'number' ? f.rolloutPercentage : 0
        }));
    }

    async toggleFeatureFlag(id: number, enabled: boolean): Promise<boolean> {
        // Backend exposes PUT /super-admin/feature-flags/{id}/toggle with body { isEnabled: boolean }.
        const res = await this.axiosInstance.put(
            `/super-admin/feature-flags/${id}/toggle`,
            { isEnabled: enabled }
        );
        return res.status >= 200 && res.status < 300;
    }

    async createFeatureFlag(data: CreateFeatureFlagRequest): Promise<FeatureFlagDto> {
        // Backend expects isEnabled/rolloutPercentage (and optional description).
        const res = await this.axiosInstance.post<{
            id: number; key: string; name: string; isEnabled: boolean; rolloutPercentage: number;
        }>('/super-admin/feature-flags', {
            key: data.key,
            name: data.name,
            isEnabled: data.enabled,
            rolloutPercentage: data.rolloutPercent
        });
        const f = res.data ?? ({} as { id: number; key: string; name: string; isEnabled: boolean; rolloutPercentage: number });
        return {
            id: f.id,
            key: f.key ?? data.key,
            name: f.name ?? data.name,
            enabled: Boolean(f.isEnabled ?? data.enabled),
            rolloutPercent: typeof f.rolloutPercentage === 'number' ? f.rolloutPercentage : data.rolloutPercent
        };
    }

    async getAuditLogs(params?: { search?: string; startDate?: string; endDate?: string; page?: number; pageSize?: number }): Promise<AuditLogListResponse> {
        // TODO(backend): /super-admin/audit-logs is not wired in lower environments and
        // returns 404. Once the real endpoint ships, delete the 404 → empty-state shim
        // below so genuine 404s surface as errors again. Tracked on AB#1337.
        const emptyResult: AuditLogListResponse = {
            logs: [],
            totalCount: 0,
            page: params?.page ?? 1,
            pageSize: params?.pageSize ?? 20
        };
        try {
            const res = await this.axiosInstance.get<{
                items?: AuditLogListResponse['logs']; logs?: AuditLogListResponse['logs']; totalCount?: number; page?: number; pageSize?: number;
            }>('/super-admin/audit-logs', { params });
            const body = res.data ?? {};
            const logs = Array.isArray(body.logs) ? body.logs : Array.isArray(body.items) ? body.items : [];
            return {
                logs,
                totalCount: body.totalCount ?? logs.length,
                page: body.page ?? emptyResult.page,
                pageSize: body.pageSize ?? emptyResult.pageSize
            };
        } catch (err: unknown) {
            if ((err as { response?: { status?: number } })?.response?.status === 404) return emptyResult;
            throw err;
        }
    }

    // Pricing Configuration (#1056)
    async getPricingConfig(params?: { configType?: string; region?: string }): Promise<PricingConfigResponse> {
        const res = await this.axiosInstance.get<PricingConfigResponse>('/super-admin/pricing-config', { params });
        return res.data ?? { configs: [] };
    }

    async updatePricingConfig(data: UpdatePricingConfigRequest): Promise<PricingConfigDto> {
        const res = await this.axiosInstance.put<PricingConfigDto>('/super-admin/pricing-config', data);
        return res.data;
    }

    async getPricingHistory(params?: { configType?: string; region?: string; page?: number; pageSize?: number }): Promise<PricingHistoryResponse> {
        const res = await this.axiosInstance.get<PricingHistoryResponse>('/super-admin/pricing-config/history', { params });
        return res.data ?? { items: [], totalCount: 0, page: 1, pageSize: 20 };
    }

    // Phase 9 — Subscription Analytics
    async getSubscriptionAnalytics(params: {
        startDate?: string;
        endDate?: string;
        granularity?: string;
        include?: string;
    }): Promise<SubscriptionAnalyticsResponse> {
        const queryParams = new URLSearchParams();
        if (params.startDate) queryParams.append('startDate', params.startDate);
        if (params.endDate) queryParams.append('endDate', params.endDate);
        if (params.granularity) queryParams.append('granularity', params.granularity);
        if (params.include) queryParams.append('include', params.include);
        const res = await this.axiosInstance.get<SubscriptionAnalyticsResponse>(
            `/super-admin/subscription-analytics?${queryParams.toString()}`
        );
        return res.data;
    }

    // Payment Dashboard (Super Admin)
    async getPaymentDashboard(params: {
        startDate?: string;
        endDate?: string;
        status?: string;
        salonId?: number;
        include?: string;
    }): Promise<PaymentDashboardResponse> {
        const queryParams = new URLSearchParams();
        if (params.startDate) queryParams.append('startDate', params.startDate);
        if (params.endDate) queryParams.append('endDate', params.endDate);
        if (params.status) queryParams.append('status', params.status);
        if (params.salonId) queryParams.append('salonId', params.salonId.toString());
        if (params.include) queryParams.append('include', params.include);
        const res = await this.axiosInstance.get<PaymentDashboardResponse>(
            `/super-admin/payment-dashboard?${queryParams.toString()}`
        );
        return res.data;
    }

    // Phase 8 — Chat
    async getConversations(): Promise<ConversationDto[]> {
        const res = await this.axiosInstance.get('/chat/conversations');
        return res.data;
    }

    async getMessages(conversationId: number, page = 1, pageSize = 50): Promise<MessageListResponse> {
        const res = await this.axiosInstance.get(`/chat/conversations/${conversationId}/messages`, { params: { page, pageSize } });
        return res.data;
    }

    async sendMessage(conversationId: number, content: string): Promise<ChatMessageDto> {
        const res = await this.axiosInstance.post(`/chat/conversations/${conversationId}/messages`, { content });
        return res.data;
    }

    // Phase 9 — Loyalty & Inventory
    async getLoyaltyProgram(): Promise<LoyaltyProgramDto | null> {
        try {
            const res = await this.axiosInstance.get('/salon-owner/loyalty');
            return res.data;
        } catch (err: unknown) {
            if ((err as { response?: { status?: number } })?.response?.status === 404) return null;
            throw err;
        }
    }

    async saveLoyaltyProgram(data: LoyaltyProgramDto): Promise<boolean> {
        const res = await this.axiosInstance.put('/salon-owner/loyalty', data);
        return res.status === 200;
    }

    async getInventoryProducts(page = 1, pageSize = 20): Promise<InventoryListResponse> {
        const res = await this.axiosInstance.get('/salon-owner/inventory', { params: { page, pageSize } });
        return res.data;
    }

    async createInventoryProduct(data: CreateInventoryProductRequest): Promise<{ id: number }> {
        const res = await this.axiosInstance.post('/salon-owner/inventory', data);
        return res.data;
    }

    // Phase 10 — API Keys & Referrals
    async getApiKeys(): Promise<ApiKeyDto[]> {
        // TODO(backend): /super-admin/api-keys is not wired in lower environments and
        // returns 404. Once the real endpoint ships, delete the 404 → empty-state shim
        // below so genuine 404s surface as errors again. Tracked on AB#1338.
        try {
            const res = await this.axiosInstance.get<ApiKeyDto[] | { items?: ApiKeyDto[] }>('/super-admin/api-keys');
            const body = res.data;
            if (Array.isArray(body)) return body;
            if (body && Array.isArray(body.items)) return body.items;
            return [];
        } catch (err: unknown) {
            if ((err as { response?: { status?: number } })?.response?.status === 404) return [];
            throw err;
        }
    }

    async generateApiKey(data: CreateApiKeyRequest): Promise<ApiKeyDto> {
        const res = await this.axiosInstance.post('/super-admin/api-keys', data);
        return res.data;
    }

    async getReferralInfo(): Promise<ReferralInfoDto> {
        const res = await this.axiosInstance.get('/salon-owner/referrals');
        return res.data;
    }

    async getReferralUsage(page = 1, pageSize = 20): Promise<ReferralUsageListResponse> {
        const res = await this.axiosInstance.get('/salon-owner/referrals/usage', { params: { page, pageSize } });
        return res.data;
    }

    // Onboarding status
    async getOnboardingStatus(): Promise<OnboardingStatusResponse> {
        const res = await this.axiosInstance.get('/onboarding/status');
        return res.data;
    }

    // Onboarding approval workflow
    async saveDraft(data: Record<string, unknown>): Promise<SaveDraftResponse> {
        const res = await this.axiosInstance.put('/onboarding/draft', data);
        return res.data;
    }

    async uploadOnboardingDocument(
        file: File,
        documentType: string,
        businessId: number,
    ): Promise<UploadDocumentResponse> {
        // Backend contract: documentType + businessId are [FromQuery]; only the
        // file itself rides in multipart form data. Packing them into FormData
        // caused all uploads to 400 (AB#1463) because ASP.NET couldn't bind
        // [FromQuery] int businessId.
        const formData = new FormData();
        formData.append('file', file);
        const qs = new URLSearchParams({
            documentType,
            businessId: String(businessId),
        });
        const res = await this.axiosInstance.post(
            `/onboarding/documents/upload?${qs.toString()}`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } },
        );
        return res.data;
    }

    async addOnboardingService(service: AddServiceRequest): Promise<AddServiceResponse> {
        const res = await this.axiosInstance.post('/onboarding/services', service);
        return res.data;
    }

    async removeOnboardingService(serviceId: number): Promise<void> {
        await this.axiosInstance.delete(`/onboarding/services/${serviceId}`);
    }

    /**
     * AB#2108 (Admin V1 P0-2) — "Submit to Devanza": moves the business to
     * PendingApproval for founder review. The backend binds `businessId` from the
     * query string and the body as `{ termsAccepted, privacyAccepted }`; in the
     * controlled-pilot configuration no signatory / licence / bank data is required.
     */
    async submitForReview(businessId: number, data: SubmitForReviewRequest): Promise<OnboardingStatusResponse> {
        const res = await this.axiosInstance.post(`/onboarding/submit?businessId=${businessId}`, data);
        return res.data;
    }

    /**
     * @deprecated AB#2108 — the backend route `POST /onboarding/activate` was removed
     * (owners can no longer self-activate; a founder approves in Pending Approvals).
     * Only the decommissioned, unrouted onboarding wizard still references this.
     */
    async activateSelfRegistered(businessId: number, data: { termsAccepted: boolean; privacyAccepted: boolean }): Promise<OnboardingStatusResponse> {
        const res = await this.axiosInstance.post(`/onboarding/activate?businessId=${businessId}`, data);
        return res.data;
    }

    // Super Admin approval endpoints
    async getPendingApprovals(): Promise<PendingApprovalListItem[]> {
        const res = await this.axiosInstance.get('/super-admin/pending-approvals');
        return res.data;
    }

    async getPendingApprovalDetail(businessId: number): Promise<PendingApprovalDetail> {
        const res = await this.axiosInstance.get(`/super-admin/pending-approvals/${businessId}`);
        // AB#2001 — coerce string enum-name dayOfWeek → 0..6 int so
        // ApprovalDetailPage's DAYS[dow] resolves (was rendering blank labels).
        return normalizePendingApprovalDetail(res.data);
    }

    async approveBusiness(businessId: number): Promise<void> {
        await this.axiosInstance.post(`/super-admin/pending-approvals/${businessId}/approve`);
    }

    async rejectBusiness(businessId: number, reason: string): Promise<void> {
        await this.axiosInstance.post(`/super-admin/pending-approvals/${businessId}/reject`, { reason });
    }

    // Financial Transactions & Reports (#849, #850, #1003)
    async getFinancialTransactions(params: FinancialTransactionsParams): Promise<FinancialTransactionsResponse> {
        const res = await this.axiosInstance.get<FinancialTransactionsResponse>('/super-admin/financial/transactions', { params });
        return res.data;
    }

    async getFinancialReport(params: FinancialReportParams): Promise<FinancialReportResponse> {
        const res = await this.axiosInstance.get<FinancialReportResponse>('/super-admin/financial/reports', { params });
        return res.data;
    }

    async exportFinancialReport(params: FinancialReportParams & { format?: string }): Promise<Blob> {
        const res = await this.axiosInstance.get('/super-admin/financial/reports/export', {
            params,
            responseType: 'blob'
        });
        return res.data;
    }

    // Platform Payouts (#1028)
    async getPlatformPayouts(params: PlatformPayoutsParams): Promise<PlatformPayoutsResponse> {
        const res = await this.axiosInstance.get<PlatformPayoutsResponse>('/super-admin/payouts', { params });
        return res.data;
    }

    async getPayoutsSummary(): Promise<PlatformPayoutSummaryDto> {
        const res = await this.axiosInstance.get<PlatformPayoutSummaryDto>('/super-admin/payouts/summary');
        return res.data;
    }

    async retryPayout(id: number): Promise<RetryPayoutResult> {
        const res = await this.axiosInstance.post<RetryPayoutResult>(`/super-admin/payouts/${id}/retry`);
        return res.data;
    }

    // ── Platform Promotions (AB#1006) — Super Admin ────────────────────────
    // Contract: arch-brief.md §7.1, base route /api/v2/superadmin/platform-promotions.
    // Mutations carry an Idempotency-Key header (arch-brief §6/§8 — every mutation
    // is [Idempotent]). A fresh UUID per call is the client side of the
    // idempotency contract; retries within React Query reuse the same value.

    /** Build a per-mutation Idempotency-Key header. */
    private idempotencyHeaders(key?: string): { headers: Record<string, string> } {
        const value = key ?? newIdempotencyKey();
        return { headers: { 'Idempotency-Key': value } };
    }

    async getPlatformPromotions(
        params?: PlatformPromotionListParams
    ): Promise<PlatformPromotionListResponse> {
        const res = await this.axiosInstance.get<PlatformPromotionListResponse>(
            '/v2/superadmin/platform-promotions',
            { params }
        );
        return (
            res.data ?? {
                items: [],
                totalCount: 0,
                pageNumber: params?.pageNumber ?? 1,
                pageSize: params?.pageSize ?? 25,
            }
        );
    }

    async getPlatformPromotionById(id: number): Promise<PlatformPromotionDto> {
        const res = await this.axiosInstance.get<PlatformPromotionDto>(
            `/v2/superadmin/platform-promotions/${id}`
        );
        return res.data;
    }

    async createPlatformPromotion(
        data: PlatformPromotionUpsertRequest,
        idempotencyKey?: string
    ): Promise<PlatformPromotionDto> {
        const res = await this.axiosInstance.post<PlatformPromotionDto>(
            '/v2/superadmin/platform-promotions',
            data,
            this.idempotencyHeaders(idempotencyKey)
        );
        return res.data;
    }

    async updatePlatformPromotion(
        id: number,
        data: PlatformPromotionUpsertRequest,
        idempotencyKey?: string
    ): Promise<PlatformPromotionDto> {
        const res = await this.axiosInstance.put<PlatformPromotionDto>(
            `/v2/superadmin/platform-promotions/${id}`,
            data,
            this.idempotencyHeaders(idempotencyKey)
        );
        return res.data;
    }

    async activatePlatformPromotion(id: number): Promise<PlatformPromotionDto> {
        const res = await this.axiosInstance.post<PlatformPromotionDto>(
            `/v2/superadmin/platform-promotions/${id}/activate`,
            null,
            this.idempotencyHeaders()
        );
        return res.data;
    }

    async pausePlatformPromotion(id: number): Promise<PlatformPromotionDto> {
        const res = await this.axiosInstance.post<PlatformPromotionDto>(
            `/v2/superadmin/platform-promotions/${id}/pause`,
            null,
            this.idempotencyHeaders()
        );
        return res.data;
    }

    async resumePlatformPromotion(id: number): Promise<PlatformPromotionDto> {
        const res = await this.axiosInstance.post<PlatformPromotionDto>(
            `/v2/superadmin/platform-promotions/${id}/resume`,
            null,
            this.idempotencyHeaders()
        );
        return res.data;
    }

    async deactivatePlatformPromotion(id: number): Promise<PlatformPromotionDto> {
        const res = await this.axiosInstance.post<PlatformPromotionDto>(
            `/v2/superadmin/platform-promotions/${id}/deactivate`,
            null,
            this.idempotencyHeaders()
        );
        return res.data;
    }

    /** Hard-delete — only valid for a Draft with 0 redemptions (else BE 409). */
    async deletePlatformPromotion(id: number): Promise<void> {
        await this.axiosInstance.delete(
            `/v2/superadmin/platform-promotions/${id}`,
            this.idempotencyHeaders()
        );
    }

    async duplicatePlatformPromotion(
        id: number,
        data: DuplicatePlatformPromotionRequest
    ): Promise<PlatformPromotionDto> {
        const res = await this.axiosInstance.post<PlatformPromotionDto>(
            `/v2/superadmin/platform-promotions/${id}/duplicate`,
            data,
            this.idempotencyHeaders()
        );
        return res.data;
    }

    async getPlatformPromotionAnalytics(
        id: number
    ): Promise<PlatformPromotionAnalyticsDto> {
        const res = await this.axiosInstance.get<PlatformPromotionAnalyticsDto>(
            `/v2/superadmin/platform-promotions/${id}/analytics`
        );
        return res.data;
    }

    async getPlatformPromotionAudit(
        id: number
    ): Promise<PlatformPromotionAuditEntryDto[]> {
        const res = await this.axiosInstance.get<PlatformPromotionAuditEntryDto[]>(
            `/v2/superadmin/platform-promotions/${id}/audit`
        );
        return res.data ?? [];
    }

    /**
     * Reads whether checkout redemption is enabled (drives the pre-ledger banner,
     * AC G.3 / arch-brief O2). Until Sara exposes the flag, a 404/absent value is
     * treated as `false` — the safe pre-ledger posture (banner shows).
     */
    async getPlatformPromotionsConfig(): Promise<PlatformPromotionsConfigDto> {
        try {
            const res = await this.axiosInstance.get<Partial<PlatformPromotionsConfigDto>>(
                '/v2/superadmin/platform-promotions/config'
            );
            return { redemptionEnabled: Boolean(res.data?.redemptionEnabled) };
        } catch (err: unknown) {
            if ((err as { response?: { status?: number } })?.response?.status === 404) {
                return { redemptionEnabled: false };
            }
            throw err;
        }
    }
}

// Super Admin Dashboard types
export interface KpiMetric {
    key: string;
    value: number;
    description: string;
    trend?: {
        percentageChange: number;
        direction: string;
        previousValue: number;
        isAvailable: boolean;
        isPositive: boolean;
    };
}

export interface SuperAdminDashboardResponse {
    overview?: { metrics: KpiMetric[] };
    revenueTrend?: { periodLabel: string; data: Array<{ date: string; revenue: number }> };
    bookingTrend?: { periodLabel: string; data: Array<{ date: string; bookings: number }> };
    statusDistribution?: { data: Array<{ status: string; value: number }> };
    serviceCategoryDistribution?: { data: Array<{ category: string; value: number }> };
}

// Subscription Analytics Types
export interface SubscriptionAnalyticsResponse {
    overview?: SubscriptionOverviewDto;
    mrrTrend?: MrrTrendChartDto;
    planDistribution?: PlanDistributionItem[];
    churnReasons?: ChurnReasonItem[];
    regionBreakdown?: RegionBreakdownDto;
    movements?: SubscriptionMovementItem[];
}

export interface SubscriptionOverviewDto {
    totalActiveSubscriptions: number;
    subscriptionsByPlan: Record<string, number>;
    mrr: number;
    mrrByPlan: Record<string, number>;
    mrrGrowthPercent: number;
    arr: number;
    newSubscriptionsCount: number;
    churnedSubscriptionsCount: number;
    churnRate: number;
    netNewSubscriptions: number;
    upgradeCount: number;
    downgradeCount: number;
    avgRevenuePerSalon: number;
    trialConversionRate: number;
}

export interface MrrTrendChartDto {
    description: string;
    data: MrrTrendDataPoint[];
}

export interface MrrTrendDataPoint {
    date: string;
    mrr: number;
    activeCount: number;
}

export interface PlanDistributionItem {
    plan: string;
    count: number;
    percentage: number;
    revenue: number;
}

export interface ChurnReasonItem {
    reason: string;
    count: number;
}

export interface RegionBreakdownDto {
    regions: Record<string, RegionMetrics>;
}

export interface RegionMetrics {
    activeSubscriptions: number;
    mrr: number;
    newSubscriptions: number;
    churnedSubscriptions: number;
}

export interface SubscriptionMovementItem {
    date: string;
    type: string;
    fromPlan: string;
    toPlan: string;
    count: number;
}

// Stripe — Payment types
export interface StripePaymentIntentResponse {
    clientSecret: string;
    paymentIntentId: string;
    amount: number;
    currency: string;
    status: string;
}

export interface StripePaymentMethodDto {
    id: string;
    brand: string;
    last4: string;
    expMonth: number;
    expYear: number;
    isDefault: boolean;
}

// Phase 5 — Payment types
export interface TransactionDto {
    id: number;
    date: string;
    appointmentId: number;
    amount: number;
    status: string;
    method: string;
    stripePaymentIntentId?: string;
    currency?: string;
    platformFeeAmount?: number;
    platformFeeRate?: number;
    promoCodeApplied?: string;
    discountAmount?: number;
    netAmount?: number;
    cardLast4?: string;
    cardBrand?: string;
}
export interface TransactionListResponse {
    transactions: TransactionDto[];
    totalCount: number;
    totalRevenue?: number;
    page: number;
    pageSize: number;
}

export interface AppointmentPaymentDetails {
    paymentId: number;
    stripePaymentIntentId: string;
    amount: number;
    currency: string;
    status: string;
    paymentMethod: string;
    paidAt: string;
    platformFeeAmount: number;
    platformFeeRate: number;
    promoCodeApplied?: string;
    discountAmount?: number;
    netAmount: number;
    cardLast4?: string;
    cardBrand?: string;
}

export interface CommissionDto {
    id: number;
    appointmentId: number;
    rate: number;
    amount: number;
    status: string;
}
export interface CommissionListResponse {
    commissions: CommissionDto[];
    totalCommission: number;
    totalCount: number;
    page: number;
    pageSize: number;
}

export interface PayoutDto {
    id: number;
    period: string;
    amount: number;
    status: string;
}
export interface PayoutListResponse {
    payouts: PayoutDto[];
    totalCount: number;
    page: number;
    pageSize: number;
}

// Phase 6 — Subscription types
// BE shape after PR #599 K1 (2026-05-03): SubscriptionPlanDto returns
// monthlyPrice + per-region monthly/annual prices + parsed features +
// trialDays. There is NO `price` or `currency` field — those were a long-
// standing FE myth that this file used to declare against the wrong
// contract. Fixed in fix/step5-price-disclosure-and-interpolation.
export interface PlanDto {
    id: number;
    name: string;
    /** PlanTier enum value (1..4) — BE serializes as a number. */
    tier: number;
    monthlyPrice: number;
    annualPrice: number;
    /** Per-region prices — same value as monthlyPrice/annualPrice today
     *  until per-region pricing is split (separate DBA decision). */
    monthlyPriceAED: number;
    monthlyPriceJOD: number;
    annualPriceAED: number;
    annualPriceJOD: number;
    trialDays: number;
    /** Pre-parsed feature bullets — BE handles JSON parsing now. */
    features: string[];
    /** Raw FeaturesJson kept for back-compat. */
    featuresJson?: string;
    isCurrent?: boolean;
}

/**
 * Mirrors backend `SubscriptionDto` exactly (see
 * `Devanza.Services/Contracts/SubscriptionContracts/SubscriptionContracts.cs`).
 *
 * AB#1933 hotfix — the previous FE shape declared three fields the BE
 * never sent (`planId`, `tier: string`, `features: string[]`), which
 * crashed `SubscriptionPage` on `currentSub.features.map(...)` and made
 * the upgrade card lookup miss every plan. Consumers that need feature
 * bullets should derive them from the matching `PlanDto` via
 * `plans.find(p => p.tier === sub.planTier)?.features`.
 *
 * `planTier` is the `PlanTier` enum (Starter=1, Growth=2, Pro=3,
 * Enterprise=4) and is serialized as a number on the wire — same shape
 * as `PlanDto.tier` and `ActivateTrialRequest.planTier`.
 */
export interface SubscriptionDto {
    id: number;
    businessId: number;
    planName: string;
    planTier: PlanTier;
    /** Mirrors backend SubscriptionStatus enum: Active=1, Expired=2,
     *  Cancelled=3, PastDue=4, Trial=5, Incomplete=6. BE serializes as
     *  a number; we accept the string alias for backwards-compat with
     *  any legacy code path still string-comparing. */
    status: number | string;
    startDate: string;
    endDate: string;
    autoRenew: boolean;
    // ── Trial fields (PBI-C, AB#1464) — populated when status ∈ { Trial, Incomplete } ──
    trialStartAt?: string;
    trialEndAt?: string;
    isFoundingMember?: boolean;
    stripeSubscriptionId?: string;
}

/** PlanTier enum — backend Devanza.Core.Enums.PlanTier */
export type PlanTier = 1 | 2 | 3 | 4;
export const PLAN_TIER = {
    Starter: 1 as PlanTier,
    Growth: 2 as PlanTier,
    Pro: 3 as PlanTier,
    Enterprise: 4 as PlanTier,
};

/** Request body for POST /api/subscriptions/activate-trial (PBI-C). */
export interface ActivateTrialRequest {
    planTier: PlanTier;
    billingName?: string;
}

/** Response from POST /api/subscriptions/activate-trial. */
export interface TrialActivationResponse {
    subscriptionId: number;
    stripeSubscriptionId: string;
    /** SetupIntent client_secret — pass to stripe.confirmCardSetup() to collect the card. */
    pendingSetupIntentClientSecret: string | null;
    trialStartAt: string;
    trialEndAt: string;
    isFoundingMember: boolean;
    /** Mirrors backend SubscriptionStatus: Active=1, Expired=2, Cancelled=3, PastDue=4, Trial=5, Incomplete=6. */
    status: number;
    planTier: number;
    currency: string;
}

/**
 * AB#1904 (W-1) — Request body for POST /api/subscriptions/checkout-session
 * (AB#1902 BE). The endpoint is JWT-authenticated; tenant is taken from the
 * BusinessId claim. Optional `Idempotency-Key` header dedups accidental
 * retries; caller mints a UUID v4 in the modal and persists to sessionStorage
 * for the lifetime of the open modal.
 */
export interface CreateCheckoutSessionRequest {
    /** PlanTier enum (Starter=1, Growth=2, Pro=3, Enterprise=4).
     *  Replaces the legacy `planId: number` field. BE model-binds the
     *  enum from either an int or its string name, but we send the int
     *  for consistency with `PlanDto.tier` and `ActivateTrialRequest`. */
    planTier: PlanTier;
    /** `true` = annual cadence, `false` = monthly. Replaces the legacy
     *  `billingCycle: 'monthly' | 'annual'` string the BE never read. */
    isAnnual: boolean;
    /** Optional FE override of the success redirect path. Defaults
     *  server-side to `/subscription/success` when omitted. */
    returnPath?: string;
    /** Optional attribution — copied through Stripe metadata into webhook payload. */
    attributionFrom?: string;
    attributionFeature?: string;
}

/**
 * AB#1904 (W-1) — Response from the Checkout-session mint endpoint.
 * `url` is the Stripe-hosted Checkout URL — FE does
 * `window.location.href = url` (NOT React Router navigate — Stripe requires
 * a full-page navigation so the iframe sandbox can run 3DS challenges).
 */
export interface CreateCheckoutSessionResponse {
    url: string;
    sessionId: string;
}

export interface InvoiceDto {
    id: number;
    date: string;
    amount: number;
    currency: string;
    status: string;
    planName: string;
}
export interface InvoiceListResponse {
    invoices: InvoiceDto[];
    totalCount: number;
    page: number;
    pageSize: number;
}

export interface NoShowPolicyDto {
    depositRequired: boolean;
    depositPercent: number;
    gracePeriodMinutes: number;
    cancellationWindowHours: number;
}

// Phase 7 — Admin types
export interface AdminUserDto {
    id: number;
    fullName: string;
    email: string;
    role: string;
    status: string;
    lastLogin: string;
}
export interface AdminUserListResponse {
    users: AdminUserDto[];
    totalCount: number;
    page: number;
    pageSize: number;
}

/** AB#2045 — a business a salon owner owns, surfaced so the SuperAdmin
 * Grant-founding-access action can target a specific business by id. */
export interface AdminOwnerBusinessRef {
    businessId: number;
    name: string;
    /** Batch C (AB#2195) — lifecycle status enum NAME ("Approved", "Suspended", …). */
    status: string;
    /** Batch C (AB#2195) — set while the salon is suspended. */
    suspensionReason: string | null;
}
export interface AdminOwnerDto {
    id: number;
    fullName: string;
    email: string;
    businessName: string;
    status: string;
    /** AB#2045 — the owner's businesses (id + name) for the grant action. */
    businesses: AdminOwnerBusinessRef[];
}
export interface AdminOwnerListResponse {
    owners: AdminOwnerDto[];
    totalCount: number;
    page: number;
    pageSize: number;
}

/** AB#2045 — plan-tier brand names, serialized as enum NAMES on the wire
 * (backend uses JsonStringEnumConverter). Kept in Latin script per the plan
 * brand-name convention. */
export type SubscriptionPlanTier = 'Starter' | 'Growth' | 'Pro' | 'Enterprise';

/** AB#2045 — body for the SuperAdmin grant-subscription endpoint. All fields
 * optional; the backend applies founder-approved defaults (Enterprise, 365d). */
export interface GrantSubscriptionRequest {
    planTier?: SubscriptionPlanTier;
    /** Comp window length in days. Backend clamps to [1, 3650]; default 365. */
    compDays?: number;
    /** Optional free-text audit reason (backend caps at 500 chars). */
    reason?: string;
}

/** AB#2045 — response from the SuperAdmin grant-subscription endpoint. */
export interface GrantSubscriptionResponse {
    subscriptionId: number;
    businessId: number;
    status: string;
    planTier: SubscriptionPlanTier;
    isComplimentary: boolean;
    startDate: string;
    endDate: string;
    grantedByUserProfileId: number | null;
    /** true when an existing comp grant was returned unchanged (idempotent). */
    alreadyGranted: boolean;
}

/**
 * P0-7 (AB#2047) — one appointment row returned by the SuperAdmin
 * booking-locate endpoint. De-identified for pilot support: customer name and
 * (nullable) phone only, no email or other PII. `status` is the enum NAME
 * ("Pending"/"Approved"/"Completed"/"Cancelled"/"NoShow"/"Arrived"/"Started")
 * because the API serializes enums as strings (JsonStringEnumConverter).
 */
export interface AdminAppointmentLocateDto {
    appointmentId: number;
    /** ISO-8601 appointment start (UTC on the wire). */
    appointmentDate: string;
    durationMinutes: number;
    status: string;
    serviceName: string;
    staffName: string | null;
    customerName: string;
    customerPhone: string | null;
    amount: number;
}

/** P0-7 (AB#2047) — query params for the SuperAdmin booking-locate endpoint. */
export interface GetBusinessAppointmentsParams {
    /** Narrow to one customer profile (optional). */
    customerProfileId?: number;
    /** ISO date/time lower bound (inclusive), optional. */
    from?: string;
    /** ISO date/time upper bound (inclusive), optional. */
    to?: string;
    pageNumber?: number;
    pageSize?: number;
}

/**
 * Batch C (AB#2195) — one booking from the Super Admin cross-salon search: the
 * same minimal customer fields as {@link AdminAppointmentLocateDto} plus the salon.
 */
export interface AdminSupportAppointmentDto extends AdminAppointmentLocateDto {
    businessId: number;
    businessName: string;
}

/** Batch C (AB#2195) — query for the Super Admin booking search. */
export interface SearchAdminAppointmentsParams {
    appointmentId?: number;
    phone?: string;
    businessId?: number;
    pageNumber?: number;
    pageSize?: number;
}

export interface AdminSupportAppointmentListResponse {
    items: AdminSupportAppointmentDto[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
}

/** Batch C (AB#2195) — result of a salon suspension. */
export interface BusinessSuspensionResult {
    status: string;
    openFutureBookings: AdminSupportAppointmentDto[];
}

function toAdminSupportAppointment(a: Partial<AdminSupportAppointmentDto>): AdminSupportAppointmentDto {
    return {
        appointmentId: a.appointmentId ?? 0,
        businessId: a.businessId ?? 0,
        businessName: a.businessName ?? '',
        appointmentDate: a.appointmentDate ?? '',
        durationMinutes: typeof a.durationMinutes === 'number' ? a.durationMinutes : 0,
        status: a.status ?? '',
        serviceName: a.serviceName ?? '',
        staffName: a.staffName ?? null,
        customerName: a.customerName ?? '',
        customerPhone: a.customerPhone ?? null,
        amount: typeof a.amount === 'number' ? a.amount : 0,
    };
}

/** P0-7 (AB#2047) — paged response from the SuperAdmin booking-locate endpoint. */
export interface AdminAppointmentListResponse {
    items: AdminAppointmentLocateDto[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
}

export interface FeatureFlagDto {
    id: number;
    key: string;
    name: string;
    enabled: boolean;
    rolloutPercent: number;
}
export interface CreateFeatureFlagRequest {
    key: string;
    name: string;
    enabled: boolean;
    rolloutPercent: number;
}

export interface AuditLogDto {
    id: number;
    timestamp: string;
    userName: string;
    action: string;
    entity: string;
    details: string;
}
export interface AuditLogListResponse {
    logs: AuditLogDto[];
    totalCount: number;
    page: number;
    pageSize: number;
}

// Pricing Configuration types (#1056)
export interface PricingConfigDto {
    id: number;
    configType: string;
    region: string;
    valuesJson: string;
    effectiveFrom: string;
    effectiveTo: string | null;
    version: number;
    isActive: boolean;
    modifiedByName: string | null;
    modifiedAt: string | null;
    approvedByName: string | null;
}

export interface PricingConfigResponse {
    configs: PricingConfigDto[];
}

export interface UpdatePricingConfigRequest {
    configType: string;
    region: string;
    valuesJson: string;
    effectiveFrom: string;
}

export interface PricingHistoryResponse {
    items: PricingConfigDto[];
    totalCount: number;
    page: number;
    pageSize: number;
}

// Phase 8 — Chat types
export interface ConversationDto {
    id: number;
    participantName: string;
    lastMessage: string;
    lastMessageAt: string;
    unreadCount: number;
}

export interface ChatMessageDto {
    id: number;
    conversationId: number;
    senderName: string;
    content: string;
    sentAt: string;
    isOwn: boolean;
}
export interface MessageListResponse {
    messages: ChatMessageDto[];
    totalCount: number;
    page: number;
    pageSize: number;
}

// Phase 9 — Loyalty & Inventory types
export interface LoyaltyProgramDto {
    id?: number;
    name: string;
    pointsPerCurrency: number;
    pointsRedemptionRate: number;
    minimumRedeemPoints: number;
    isActive: boolean;
}

export interface InventoryProductDto {
    id: number;
    name: string;
    sku: string;
    stock: number;
    threshold: number;
    expiryDate: string;
}
export interface InventoryListResponse {
    products: InventoryProductDto[];
    totalCount: number;
    page: number;
    pageSize: number;
}
export interface CreateInventoryProductRequest {
    name: string;
    sku: string;
    stock: number;
    threshold: number;
    expiryDate: string;
}

// Phase 10 — API Keys & Referrals types
export interface ApiKeyDto {
    id: number;
    name: string;
    key?: string;
    scopes: string[];
    rateLimit: number;
    status: string;
    createdAt: string;
}
export interface CreateApiKeyRequest {
    name: string;
    scopes: string[];
    rateLimit: number;
}

export interface ReferralInfoDto {
    referralCode: string;
    totalReferrals: number;
    successfulReferrals: number;
    pendingReferrals: number;
    totalEarnings: number;
}

export interface ReferralUsageDto {
    id: number;
    referredName: string;
    referredEmail: string;
    date: string;
    status: string;
    reward: number;
}
export interface ReferralUsageListResponse {
    usages: ReferralUsageDto[];
    totalCount: number;
    page: number;
    pageSize: number;
}

// Onboarding status types
/** Backend `BusinessStatus`, serialised as its enum name (or number). */
export type BackendBusinessStatus = 'Draft' | 'PendingApproval' | 'Approved' | 'Rejected' | 'Suspended';

/**
 * GET /onboarding/status (and POST /onboarding/submit) — backend
 * `OnboardingStatusResponse`. AB#2108: aligned to the real wire shape; the old
 * type described fields the backend never sends (`businessStatus`, `currentStep`,
 * `*Completed`). With no business the backend returns `businessId: 0`.
 */
export interface OnboardingStatusResponse {
    businessId: number;
    businessName?: string | null;
    businessType?: string | null;
    status: BackendBusinessStatus | number;
    steps?: Array<{ stepName: string; completed: boolean }>;
    isFullyCompleted: boolean;
    submittedForReviewAt?: string | null;
    approvedAt?: string | null;
    rejectedAt?: string | null;
    rejectionReason?: string | null;
    /** Batch C (AB#2195) — set while Devanza has suspended the salon. */
    suspendedAt?: string | null;
    suspensionReason?: string | null;
}

// Onboarding approval workflow types
export interface SaveDraftResponse {
    businessId: number;
    status: string;
}

export interface UploadDocumentResponse {
    documentId: number;
    fileUrl: string;
}

export interface AddServiceRequest {
    name: string;
    category: string;
    duration: string;
    price: number;
}

export interface AddServiceResponse {
    id: number;
    name: string;
    category: string;
    duration: string;
    price: number;
}

/**
 * POST /onboarding/submit body — backend `SubmitForReviewRequest`. The
 * authorised-signatory name is a compliance field the controlled pilot does not
 * collect (AB#2108); the backend requires it only when compliance fields are on.
 */
export interface SubmitForReviewRequest {
    termsAccepted: boolean;
    privacyAccepted: boolean;
    authorizedSignatoryName?: string;
}

/**
 * GET /super-admin/pending-approvals — backend `PendingApprovalListItem`
 * (AB#2108: aligned to the wire; null properties are omitted by the API).
 */
export interface PendingApprovalListItem {
    businessId: number;
    businessName: string;
    businessType?: string | null;
    ownerName: string;
    submittedAt?: string | null;
    documentCount: number;
    city?: string | null;
    country?: string | null;
}

/**
 * GET /super-admin/pending-approvals/{id} — backend
 * `PendingApprovalDetailResponse` (AB#2108: aligned to the wire). Location is
 * flat; `bankDetails` and `license` are omitted when absent (always, for
 * controlled-pilot submissions).
 */
export interface PendingApprovalDetail {
    businessId: number;
    businessName: string;
    businessType?: string | null;
    about?: string | null;
    serviceType: string;
    status: string;
    submittedForReviewAt?: string | null;
    ownerName: string;
    country?: string | null;
    city?: string | null;
    state?: string | null;
    streetAddress?: string | null;
    latitude?: number | null;
    longitude?: number | null;
    businessHours: Array<{
        dayOfWeek: number;
        isOpen: boolean;
        openTime: string | null;
        closeTime: string | null;
        is24Hours?: boolean;
    }>;
    services: Array<{
        id: number;
        name: string;
        categoryName?: string | null;
        /** TimeSpan, "hh:mm:ss". */
        minDuration: string;
        price?: number | null;
    }>;
    bankDetails?: {
        bankName: string;
        accountHolderName: string;
        ibanMasked: string;
        swiftCode?: string | null;
        currency: string;
    } | null;
    license?: {
        licenseNumber: string;
        expiryDate: string;
        businessRegistrationNumber?: string | null;
        taxRegistrationNumber?: string | null;
        status: string;
    } | null;
    documents: Array<{
        id: number;
        type: string;
        fileName: string;
        fileUrl: string;
        fileSizeBytes?: number;
    }>;
}

// Payment Dashboard Types
export interface PaymentDashboardResponse {
    kpis?: PaymentDashboardKpis;
    paymentsTrend?: PaymentTrendDataPoint[];
    commissionTrend?: CommissionTrendDataPoint[];
    payoutStatus?: PayoutStatusBreakdown;
    paymentMethods?: Record<string, number>;
}

export interface PaymentDashboardKpis {
    totalProcessed: CountAmountDto;
    successful: CountAmountDto;
    failed: CountAmountDto;
    pending: CountAmountDto;
    commissionEarned: number;
    payoutsCompleted: number;
    payoutsPending: number;
    refunds: CountAmountDto;
}

export interface CountAmountDto {
    count: number;
    amount: number;
}

export interface PaymentTrendDataPoint {
    date: string;
    successful: number;
    failed: number;
    amount: number;
}

export interface CommissionTrendDataPoint {
    date: string;
    amount: number;
}

export interface PayoutStatusBreakdown {
    completed: number;
    pending: number;
    failed: number;
}

// Financial Transactions & Reports types (#849, #850, #1003)
export interface FinancialTransactionsParams {
    startDate?: string;
    endDate?: string;
    transactionType?: string;
    status?: string;
    settlementStatus?: string;
    amountMin?: number;
    amountMax?: number;
    salonId?: number;
    search?: string;
    page?: number;
    pageSize?: number;
    sortBy?: string;
    sortDirection?: string;
}

export interface FinancialTransactionsResponse {
    items: FinancialTransactionDto[];
    totalCount: number;
    page: number;
    pageSize: number;
}

export interface FinancialTransactionDto {
    id: number;
    stripePaymentIntentId: string | null;
    bookingId: number | null;
    customerName: string;
    salonName: string;
    serviceName: string;
    transactionType: string;
    status: string;
    paymentMethod: string;
    amount: number;
    commissionAmount: number;
    netAmount: number;
    currency: string;
    transactionDate: string;
    settlementStatus: string;
}

export interface FinancialReportParams {
    startDate: string;
    endDate: string;
    salonId?: number;
    bookingStatus?: string;
    paymentStatus?: string;
    settlementStatus?: string;
}

export interface FinancialReportResponse {
    summary: ReportSummaryDto;
    salonBreakdown: SalonReportBreakdown[];
}

export interface ReportSummaryDto {
    totalBookings: number;
    totalAuthorized: number;
    totalCaptured: number;
    totalRefunded: number;
    totalFailed: number;
    commissionEarned: number;
    netPayableToSalons: number;
    pendingSettlement: number;
}

export interface SalonReportBreakdown {
    salonId: number;
    salonName: string;
    totalRevenue: number;
    commissionDeducted: number;
    netPayable: number;
    settlementStatus: string;
}

// Platform Payouts types (#1028)
export interface PlatformPayoutsParams {
    startDate?: string;
    endDate?: string;
    status?: number;
    salonId?: number;
    search?: string;
    page?: number;
    pageSize?: number;
}

export interface PlatformPayoutsResponse {
    items: PayoutListItemDto[];
    totalCount: number;
    page: number;
    pageSize: number;
    summary: PlatformPayoutSummaryDto;
}

export interface PayoutListItemDto {
    id: number;
    salonName: string;
    salonId: number;
    totalAmount: number;
    commissionDeducted: number;
    netAmount: number;
    status: number;
    periodStart: string;
    periodEnd: string;
    processedAt: string | null;
    bankReference: string | null;
    currency: string;
    commissionCount: number;
}

export interface PlatformPayoutSummaryDto {
    totalPending: number;
    totalProcessedThisPeriod: number;
    failedCount: number;
    totalPayoutsCount: number;
}

export interface RetryPayoutResult {
    success: boolean;
    message: string;
    newStatus: number;
}

/* ────────────────────────────────────────────────────────────────────────
 * AB#1842 (PBI-F) — Gender-mismatch override types.
 *
 * Owners submit an override request when they need to keep mismatched staff
 * (e.g. a male staff at a women-only salon for a non-customer-facing role
 * like deliveries). Super Admin reviews and Approves / Rejects via the same
 * resource (PATCH on the override id). See Faris's brief on AB#1842.
 * ────────────────────────────────────────────────────────────────────────── */

export type GenderOverrideStatus = 'Pending' | 'Approved' | 'Rejected';

/** Owner-side: minimum payload to request an override. Justification ≥ 20 chars. */
export interface RequestGenderOverrideRequest {
    justification: string;
}

/** Super Admin-side: approve or reject a pending override. */
export interface DecideGenderOverrideRequest {
    decision: Exclude<GenderOverrideStatus, 'Pending'>;
    adminReason: string;
}

/** Shape returned by the override list endpoints. Keeps both
 *  Pending and historical (Approved/Rejected) decisions in one shape so
 *  the audit log can render the same row template. */
export interface GenderOverrideListItem {
    id: number;
    businessId: number;
    businessName: string;
    businessServiceType: 'MenOnly' | 'WomenOnly' | 'Everyone';
    staffId: number;
    staffName: string;
    staffGender: 'Male' | 'Female';
    requestedByUserId: number;
    requestedByName: string;
    requestedByEmail?: string | null;
    justification: string;
    status: GenderOverrideStatus;
    createdAt: string;
    decidedByUserId: number | null;
    decidedByName?: string | null;
    decidedAt: string | null;
    adminReason: string | null;
}

export const apiService = new ApiService();
